import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { creditsLine, paywallOf, tokensFundable } from '../billing/credits';
import type { BoardroomChatChanged, BoardroomDeliverable, BoardroomDismissedNotice, BoardroomNotice, BoardroomObservation, BoardroomDeliverables, BoardroomItem, BoardroomPersona, BoardroomWhoRanks, Paywall, BoardroomDetail, BoardroomLead} from '../types';
import FilterRail from '../components/boardroom/FilterRail';
import ItemFeed from '../components/boardroom/ItemFeed';
import ItemDrawer from '../components/boardroom/ItemDrawer';
import AddItemForm, { type NewItem } from '../components/boardroom/AddItemForm';
import PaywallNotice from '../components/common/PaywallNotice';
import { AppealActivity, LookupActivity, TaggingActivity } from '../components/boardroom/ActivityPanel';
import PersonaStrip from '../components/boardroom/PersonaStrip';
import ReaderSubjects from '../components/boardroom/ReaderSubjects';
import StageModelsWidget from '../components/common/StageModelsWidget';
import { useShowInternals } from '../contexts/InternalsContext';
import { agentSettingsUrl, useReturnToHere } from '../components/common/AgentModelChip';
import ShareArtifactControl from '../components/common/ShareArtifactControl';
import Reports from '../components/boardroom/reports/Reports';
import { columnChange } from '../components/boardroom/reports/columnChange';
import ProfileView from '../components/boardroom/reports/ProfileView';
import TrackThis from '../components/boardroom/TrackThis';
import WatchPanel from '../components/boardroom/WatchPanel';
import NextDig from '../components/boardroom/reports/NextDig';
import DigModal from '../components/boardroom/reports/DigModal';
import DirectorNote, { Worklists } from '../components/boardroom/reports/DirectorNote';
import Drawer from '../components/common/Drawer';
import BoardroomChatPane from '../components/boardroom/BoardroomChatPane';
import DeliverableBoard from '../components/boardroom/deliverables/DeliverableBoard';
import DeliverableBrowser from '../components/boardroom/deliverables/DeliverableBrowser';
import RunsPanel from '../components/boardroom/RunsPanel';
import NoticeTray from '../components/boardroom/deliverables/NoticeTray';
import ObservationTray from '../components/boardroom/deliverables/ObservationTray';
import RecipePicker from '../components/boardroom/RecipePicker';
import RoomStatusBar from '../components/boardroom/RoomStatusBar';
import WhoRanksReport from '../components/boardroom/reports/WhoRanksReport';
import ListCardPane from '../components/boardroom/reports/ListCardPane';
import ReportCardPane from '../components/boardroom/reports/ReportCardPane';
import ProfilePane from '../components/boardroom/reports/ProfilePane';
import ChatMarkdown from '../components/common/ChatMarkdown';
import { framesAwaiting } from '../components/boardroom/reports/frameAxes';
import FrameProposalBanner from '../components/boardroom/FrameProposalBanner';
import FrameLine from '../components/boardroom/reports/FrameLine';
import AnswerLine from '../components/boardroom/reports/AnswerLine';
import AnswerBrief from '../components/boardroom/reports/AnswerBrief';
import RoomMenu from '../components/boardroom/RoomMenu';
import LensBar from '../components/boardroom/reports/LensBar';
import LocationBar, { type PathCrumb } from '../components/boardroom/LocationBar';
import LastDigSummary from '../components/boardroom/reports/LastDigSummary';
import TableSummary, { nameLinks } from '../components/boardroom/reports/TableSummary';
import StartForm from '../components/boardroom/StartForm';
import NotesPanel from '../components/boardroom/NotesPanel';
import SharedPanel from '../components/boardroom/SharedPanel';
import LeadBar from '../components/boardroom/leads/LeadBar';
import FollowLead from '../components/boardroom/leads/FollowLead';
import LensPhrase from '../components/boardroom/reports/LensPhrase';
import LeadsPanel from '../components/boardroom/leads/LeadsPanel';
import { insideLead, lensOfView, narrowsPast, viewForLead, withLead } from '../components/boardroom/leads/leadLens';
import { addedWords } from '../components/boardroom/reports/ReconRunsReport';
import { feedTime } from '../components/boardroom/feedTime';
import FindBox, { type FindTarget } from '../components/boardroom/FindBox';
import KeepPage from '../components/boardroom/KeepPage';
import useLiveTick from '../components/boardroom/useLiveTick';
import { viewPhrase } from '../components/boardroom/viewPhrase';
import TagsPanel from '../components/boardroom/TagsPanel';
import { backStep, crumbKey, lensOf, loadTrail, placeFromSearch, placeLabel, placeToSearch, sameLens, saveTrail, stepTrail, tableBefore, withLens, type Crumb, type Lens, type Place } from '../components/boardroom/reports/place';
import { loadHistory, nameVisits, pushVisit, saveHistory, visitKey, type Visit } from '../components/boardroom/reports/place';
import RoomBrief from '../components/boardroom/reports/RoomBrief';
import BriefNoticed from '../components/boardroom/reports/BriefNoticed';
import ComparePanel, { parseCompare, storedPicks, withFirst } from '../components/boardroom/reports/ComparePanel';
import { isStoryRoom, viewTitle } from '../components/boardroom/deliverables/views';
import ViewPicker from '../components/boardroom/deliverables/ViewPicker';
import { DockedPanel, groupOf, PanelTabs, PinButton, RightRail, usePanels, type GroupKey, type PanelKey, type RailTab } from '../components/boardroom/RightRail';
import {
  PROFILE_KINDS,
  stagedHasContent,
  REPORT_TABS,
  digFocus,
  scopeLabel,
  isCardKind,
  isCut,
  cutLens,
  type StagedDig,
} from '../components/boardroom/reports/reportsView';
import BrandSection from '../components/boardroom/reports/BrandSection';
import BrandPane from '../components/boardroom/reports/BrandPane';
import NoticePreview from '../components/boardroom/reports/NoticePreview';
import { TableFilterBox, TableFilterContext, filterWords, previewText, textMatches } from '../components/boardroom/reports/tableFilter';
import { LensTagsContext, splitLensTags } from '../components/boardroom/lensTags';
import { defaultAim, runLabel, scopeByKey, scopesFor, type Aim, type AimScope } from '../components/boardroom/reports/researchAim';
import { BUDGET_OPTIONS, clampBudget, rememberBudget, rememberedBudget } from '../components/boardroom/intents';
import BudgetPicker from '../components/boardroom/BudgetPicker';
import { ResizeHandle, handleEdgeForPaneOn, useResizablePane } from '../components/useResizablePane';
import { KIND_LABELS, LOOKUP_KINDS } from '../components/boardroom/kinds';
import {
  EMPTY_LOOKUP_COUNTS,
  EMPTY_VIEW,
  VERIFIED_OPTIONS,
  applyEvent,
  matchesView,
  tagsLabel,
  toggleTag,
  upsertItem,
  viewIsSet,
  type RoomState,
  type View,
} from '../components/boardroom/filters';
import type { BoardroomFacets, BoardroomProfile, BoardroomReconRun, BoardroomReport, BoardroomReportKey, BoardroomRoomBrief, BoardroomCompare, BoardroomRun, BoardroomRunIntent, BoardroomTableDig, SortingWaiting, SortingWaitingPick } from '../types';
import { bucket, markSeen, type Buckets } from '../components/boardroom/reportBuckets';
import type { BoardPanel } from '../components/boardroom/deliverables/BoardList';
import { clearBuckets, loadBuckets, noteFresh, saveBuckets } from '../components/boardroom/reportBuckets';
import { FreshProvider } from '../components/boardroom/useSeen';

/** One empty set, so "no insertions yet" is never a new object each render. */
const NO_FRESH: ReadonlySet<number> = new Set();
// What the feed is narrowed to, named for the "Nothing matches" line.
const WAITING_LABELS: Record<SortingWaiting, string> = {
  axes: 'not tagged yet',
  content: 'not tagged for what it holds yet',
  appeal_reading: 'Appeal: reading',
  appeal_waiting: 'Appeal: waiting to be read',
  appeal_read: 'Appeal: read',
  appeal_no_pages: 'Appeal: no pages to read yet',
};

/**
 * The room. Composition only — the state lives in `filters.ts` (pure, tested),
 * the rows in `ItemCard`, the detail in `ItemDrawer`.
 *
 * Three columns on a wide screen: the buckets, the feed, the people. Same
 * proportions as v1's boardroom (nav rail | stage | rail), fixed rather than
 * draggable for now.
 */

// The tab rail down the right edge, and the space between columns.
const RAIL_AND_GAPS = 32 + 48;
const CHAT_PANE = 340;
const CHAT_PANE_MIN = 280;
const CHAT_PANE_MAX = 620;
const CHAT_PANE_KEY = 'jcseo.boardroom.chat.width';
// The Research window's last choice, per room: `${AIM_KEY}.${roomId}`.
const AIM_KEY = 'jcseo.boardroom.research.aim';
const STAGE_MIN = 520;
// How often the status bar re-counts what a running dig has found.
const FOUND_REFRESH_MS = 5000;
// WIDE ENOUGH FOR A BOARD AND A CONVERSATION beside it. The old right rail's
// 300px is the chat's now, so the threshold is the same width for a different
// column — below it the chat overlays instead.
/** What each side panel is called, and how wide it slides out. */
const GROUP_TITLE: Record<GroupKey, string> = {
  chat: 'Ask the room',
  tags: 'Tags',
  activity: 'Activity',
  settings: 'Room settings',
};
const PANEL_TITLE = (key: PanelKey) => GROUP_TITLE[groupOf(key)];
/** Each panel's name in the strip at the top of its tab. */
const PANEL_LABEL: Record<PanelKey, string> = {
  feed: 'Feed',
  chat: 'Ask the room',
  brief: 'Question & setup',
  runs: 'Runs',
  tags: 'Tags',
  shared: 'Shared',
  leads: 'Leads',
  notes: 'Agent notes',
};
// ONE WIDTH PER TAB, so switching panels inside it does not resize it.
const GROUP_WIDTH: Record<GroupKey, string> = {
  chat: 'w-[34rem] max-w-full',
  tags: 'w-[34rem] max-w-full',
  activity: 'w-[60rem] max-w-full',
  settings: 'w-[44rem] max-w-full',
};

// WIDE ENOUGH TO PIN A PANEL beside the page. It used to count a filter
// column on the left as well, which no longer exists — so a window with room
// for the page and the chat read as narrow, and nothing could be pinned.
const WIDE_MQ = `(min-width: ${STAGE_MIN + CHAT_PANE + RAIL_AND_GAPS}px)`;
const PAGE = 100;
// One empty tally, so a room whose own has not loaded yet does not re-render forever.
const NO_BUCKETS: Buckets = {};

function useWide(): boolean {
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(WIDE_MQ).matches : true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const mq = window.matchMedia(WIDE_MQ);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}

/** Rooms for the location bar's switcher: the newest fifty, or those matching. */
const loadRooms = (q: string) => api.listBoardrooms({ limit: 50, q: q || null }).then(page => page.items);

export default function BoardroomRoom() {
  const { boardroomId } = useParams<{ boardroomId: string }>();
  const id = Number(boardroomId);
  const { subscribe } = useWebSocket();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  // Admin AND internals revealed: the gate for the debug-only controls.
  const showInternals = useShowInternals();
  const settingsReturn = useReturnToHere('the recon');
  const wide = useWide();
  // THE CHAT STAYS OPEN. It is where the room is aimed, and a drawer that has
  // to be opened every time is a channel people forget they have — which is how
  // a room ends up being steered by nobody. Docked, resizable and collapsible
  // to an edge tab, exactly like the feed's rail on the other side.
  const chat = useResizablePane({
    storageKey: CHAT_PANE_KEY,
    initial: CHAT_PANE,
    min: CHAT_PANE_MIN,
    max: CHAT_PANE_MAX,
    handleOn: handleEdgeForPaneOn('right'),
  });
  // THE SIDE PANELS: feed & filters, the seats, the chat, the brief, the
  // answer and history — each from a tab on the right edge, sliding out or
  // pinned into the one slot beside the page (see RightRail). The slot keeps
  // the chat's old width setting.
  const panels = usePanels(wide);
  const setDirectorOpen = (o: boolean) => (o ? panels.show('brief') : panels.close('brief'));
  // THE ANSWER IS IN "THE ROOM" now, with the brief (key 'brief').
  const setAnswerOpen = (o: boolean) => (o ? panels.show('brief') : panels.close('brief'));
  // "In the room" is gone: its lookups and sorting are in Feed & filters, its
  // seats in Recon runs - where a link to the seats lands.
  const setPeopleOpen = (o: boolean) => (o ? panels.show('runs') : panels.close('runs'));
  const setChatOpen = (o: boolean) => (o ? panels.show('chat') : panels.close('chat'));

  const [state, setState] = useState<RoomState | null>(null);
  // THE PLACE LIVES IN THE URL. Stage, report, profile and lens are read from
  // the address, so the browser's back button walks the room, a refresh keeps
  // your place, and a profile can be pasted to someone. A stage change pushes;
  // a lens change replaces (same place, looked at differently).
  const location = useLocation();
  const navigate = useNavigate();
  // THE BOARD IS WHERE A ROOM OPENS. An address naming a table still opens it.
  const place = useMemo<Place>(() => placeFromSearch(location.search, 'board'), [location.search]);
  const { view } = place;
  // EACH STEP REMEMBERS WHICH STEP IT WAS TAKEN FROM (its crumb key), so our
  // Back can be the browser's Back when the two would land in the same place.
  // A replace is the same step looked at differently, so it keeps what it had.
  const stepFrom = () => ({ from: crumbKey(place) });
  const go = (next: Place, replace = false) =>
    navigate({ search: placeToSearch(next) }, { replace, state: replace ? location.state : stepFrom() });
  // Back, the location bar and History: the saved place, with the lens you have now.
  const goSaved = (search: string) => navigate({ search: withLens(search, lensOf(view)) }, { state: stepFrom() });
  // THE BROWSER'S OWN BACK BUTTON restores an address with the lens it had
  // then. A lens change never adds a step (it replaces the address), so Back
  // was never a way to undo one: keep the lens you had, as our Back does.
  const navType = useNavigationType();
  // Kept with its room: another room's tags mean nothing here.
  const lastLens = useRef<{ room: number; lens: Lens } | null>(null);
  useEffect(() => {
    const prev = lastLens.current?.room === id ? lastLens.current.lens : null;
    const now = lensOf(view);
    if (prev && navType === 'POP' && !sameLens(prev, now)) {
      navigate({ search: withLens(location.search, prev) }, { replace: true, state: location.state });
      return;
    }
    lastLens.current = { room: id, lens: now };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);
  const setView = (next: View | ((v: View) => View)) =>
    go({ ...place, view: typeof next === 'function' ? next(place.view) : next }, true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Somewhere to say a thing WORKED. The page could only report failures, so a
  // save that succeeded read as a click that did nothing.
  const [note, setNote] = useState<string | null>(null);
  // A 402 from starting a run is an offer, not an error: it renders under the
  // Dig buttons with a link to billing and clears on the next successful start.
  const [paywall, setPaywall] = useState<Paywall | null>(null);
  const { credits, priceBook, plans, planOf, has } = useEntitlements();
  // THE CEILING IS THE LOWER OF THE PLAN AND THE BALANCE. The server clamps a
  // run to what credits fund and says nothing, so a 1M pick on a balance worth
  // 34k started, did four turns, ended "spent the budget", and never mentioned
  // the million. The picker offers what can actually be spent instead.
  const planBudget = plans[planOf('BOARDROOM')]?.max_token_budget ?? null;
  const fundable = tokensFundable(credits.balance, priceBook);
  const maxBudget =
    fundable === null ? planBudget : planBudget === null ? fundable : Math.min(planBudget, fundable);
  const budgetCappedByCredits = fundable !== null && (planBudget === null || fundable < planBudget);
  // THE DRAWER HAS A TRAIL TOO. Opening from the feed or a report starts a
  // fresh one; a link followed inside the drawer pushes; Back pops.
  // Per-report tallies of what arrived while you were elsewhere.
  // UNSEEN SURVIVES THE TAB CLOSING. A dig finishes while you are away and its
  // work is still marked when you come back — that is the whole point of not
  // ageing on a clock.
  //
  // Kept WITH the room they count, so moving to another room cannot save this
  // room's tallies under that one's key in the render before they reload.
  const [bucketState, setBucketState] = useState<{ room: number; buckets: Buckets }>({ room: 0, buckets: {} });
  const buckets = bucketState.room === id ? bucketState.buckets : NO_BUCKETS;
  const setBuckets = useCallback(
    (next: (b: Buckets) => Buckets) => setBucketState(s => ({ room: s.room, buckets: next(s.buckets) })),
    [],
  );
  useEffect(() => {
    if (id) setBucketState({ room: id, buckets: loadBuckets(id) });
  }, [id]);
  useEffect(() => {
    if (bucketState.room) saveBuckets(bucketState.room, bucketState.buckets);
  }, [bucketState]);
  // WHICH MARKED ROWS ARRIVED, as opposed to changed. Kept with the room for
  // the same reason the buckets are, and read through context by every table
  // rather than threaded as a sixteenth optional prop: it is a hint about
  // colour, not data about a report.
  const [freshState, setFreshState] = useState<{ room: number; ids: ReadonlySet<number> }>({
    room: 0,
    ids: NO_FRESH,
  });
  const freshIds = freshState.room === id ? freshState.ids : NO_FRESH;
  useEffect(() => {
    if (id) setFreshState({ room: id, ids: NO_FRESH });
  }, [id]);
  // A kind being moved on or off the tables — the page is refetching.
  const [sortingKinds, setSortingKinds] = useState(false);
  const [drawerTrail, setDrawerTrail] = useState<BoardroomItem[]>([]);
  const open = drawerTrail.length ? drawerTrail[drawerTrail.length - 1] : null;
  const setOpen = (item: BoardroomItem | null) => setDrawerTrail(item ? [item] : []);
  const pushOpen = (item: BoardroomItem) =>
    setDrawerTrail(t => (t.length && t[t.length - 1].id === item.id ? t : [...t.filter(x => x.id !== item.id), item]));
  const replaceOpen = (item: BoardroomItem) => setDrawerTrail(t => (t.length ? [...t.slice(0, -1), item] : [item]));
  const [lastRun, setLastRun] = useState<BoardroomRun | null>(null);
  // REPORTS ARE THE ROOM. It opens on a table that reads without a filter,
  // full width; the feed, with the rail of buckets it needs, is a side view at
  // the left edge for the rare look at every item. Not remembered: a room
  // always opens on Reports unless the address says otherwise.
  const { stage, report, profileId } = place;
  // EVERY TABLE OPENS ON THE DASHBOARD, as the open view (`openView`). There
  // used to be a page of tables behind tabs as well; the Dashboard and All
  // views do its jobs now. Opening one is a step, so Back returns from it.
  const setReport = (r: BoardroomReportKey, replace = false) => openView(r, '', replace);
  // A table or report the chat built, or a cut of a built-in table: its kind
  // plus its subject.
  const openCard = (kind: string, subject: string, replace = false) => openView(kind, subject, replace);
  // THE FEED IS A PANEL NOW, not a page. An old link or History entry that
  // says `v=feed` lands on the board with the Feed panel open, rather than on
  // a page with no filters and no way back.
  useEffect(() => {
    if (stage !== 'feed') return;
    go({ ...place, stage: 'board' }, true);
    panels.show('feed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  // THE TABLES PAGE IS GONE TOO. An old link, crumb or History entry for it
  // opens the table it named on the Dashboard; one that named no table lands
  // on the Dashboard itself.
  useEffect(() => {
    if (stage !== 'reports') return;
    const q = new URLSearchParams(location.search);
    if (q.get('r') || q.get('s')) openView(report, place.subject ?? '', true);
    else go({ ...place, stage: 'board', expanded: null }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  const [reportData, setReportData] = useState<BoardroomReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  // The axis the landscape is crossed by; null = the server's default. One,
  // not a pair: the landscape draws a section per axis, so the axis running
  // down the side is a property of the section rather than a thing to choose.
  // null = the server's pick; '' = plain lists; else a dimension.
  const [crossBy, setCrossBy] = useState<string | null>(null);
  // THE LAST SPEND YOU CHOSE, per browser. This started at the default on every
  // load, and since the page's number is the one every dig sends, the one the
  // picker remembered was never used.
  const [budget, setBudgetState] = useState(() => rememberedBudget());
  const setBudget = (n: number) => {
    setBudgetState(n);
    rememberBudget(n);
  };
  // A PROFILE is a place you went: one company, product or type in full.
  const [profile, setProfile] = useState<BoardroomProfile | null>(null);
  // THE NEXT DIG, lined up but not started. A report's Dig button stages its
  // directive here; Dig deeper starts it; Dig wider clears it.
  const [staged, setStaged] = useState<StagedDig | null>(null);
  const templateDig = (
    directive: string,
    intent: 'WIDER' | 'DEEPER',
    focus: { archetype: string; item_id: number | null; scope?: 'item' | 'table' } | null,
    source: string,
  ): StagedDig => ({ directive, intent, archetype: focus?.archetype ?? null, item_id: focus?.item_id ?? null, scope: null, source });
  const stageDig = (...args: Parameters<typeof templateDig>) => setStaged(templateDig(...args));
  // THE BROAD DIGS ARE WRITTEN AS BRIEFS: into one item (its page's own Dig),
  // a whole table (a report's Dig), or everything inside the lens. A job per
  // seat — each with its own items for a table or a lens — staged for editing
  // before anything spends. When a brief cannot be written, what the button
  // always staged stands in, so the click is never dead.
  type DigRequest =
    | { kind: 'item'; itemId: number }
    | { kind: 'table'; report: BoardroomReportKey }
    | { kind: 'lens' }
    // THE WHOLE ROOM: no table, no lens, nothing narrowed. What the top-level
    // buttons always did, now one option among the others rather than the
    // only thing those buttons could mean.
    | { kind: 'room' };
  // THE DIG, IN A MODAL: "researching" while the brief is written, then every
  // prompt that will go out, with Dig and Cancel. `ticket` drops a brief that
  // lands after the modal was cancelled, or after another dig was opened.
  const [digModal, setDigModal] = useState<
    { title: string; dig: StagedDig | null; ticket: number; aim?: Aim; scopes?: AimScope[] } | null
  >(null);
  const digTicket = useRef(0);
  // WHAT A CHOSEN AIM ASKS FOR. The two questions the window puts to a person
  // become the request that drafts the brief, so changing either re-drafts and
  // the prompts on screen always belong to what is selected above them.
  const requestFor = (aim: Aim, scopes: AimScope[]): DigRequest => {
    const s = scopeByKey(scopes, aim.scope);
    if (s.kind === 'item' && s.itemId) return { kind: 'item', itemId: s.itemId };
    if (s.kind === 'table' && s.report) return { kind: 'table', report: s.report as BoardroomReportKey };
    if (s.kind === 'lens') return { kind: 'lens' };
    return { kind: 'room' };
  };

  // WHAT A DIG WILL SEND, worked out — the board's own holes where they say
  // enough, a written brief where they do not. Returns the dig; showing it is
  // the caller's business. `onBriefing` is told when a brief is being written,
  // so whoever is waiting can say so.
  const buildDig = async (
    req: DigRequest,
    fallback: () => StagedDig,
    // WHAT THE PERSON ASKED FOR, not what the brief path assumed. Every draft
    // used to come back DEEPER, so choosing "find more" and then watching the
    // room corroborate what it already had would have been the same bug again
    // one level up.
    depth: 'WIDER' | 'DEEPER' = 'DEEPER',
    // SET ONLY BY THE RESEARCH WINDOW: what the person chose, for the plan's
    // words. A bare Dig button elsewhere passes nothing and starts from its
    // own template (`fallback`).
    aimedAt?: AimScope,
  ): Promise<StagedDig> => {
    // THE TABLE THE PERSON IS LOOKING AT, carried on the run itself.
    //
    // The brief only steers the FIRST round; the director rewrites the
    // worklists every round afterwards and used to drift back to whatever the
    // room was working toward — so you pressed Dig on the price map, watched
    // one round of pricing, and then watched competitors fill for the rest of
    // the budget. `tables` is what makes it hold.
    const scopeFor = (base: StagedDig['scope']): StagedDig['scope'] =>
      req.kind === 'table'
        ? { ...(base ?? { tags: [], verified: null, by: null, sourced: null }), tables: [req.report] }
        : base;
    const aimed = (d: StagedDig): StagedDig => ({ ...d, intent: depth, scope: scopeFor(d.scope) });
    let dig: StagedDig;

    // THE RESEARCH WINDOW NEVER WAITS.
    //
    // A brief is a model call that reads the board and writes each seat a job.
    // It was worth it when nothing else knew what a dig should do — but by the
    // time you have answered BOTH questions, everything needed is already on
    // screen or already computed:
    //
    //   fill the gaps  the table's holes say which items are short and of
    //                  what, and routing already sends each to its owner
    //   find more      there is nothing to route: it is an intent, a budget
    //                  and a scope, which is exactly what the old "Dig wider"
    //                  button sent and it needed no brief either
    //   a lens         the scope narrows the items; the seats read the lens
    //   a row          its own page already carries its dig
    //
    // So the brief survives only where it is asked for by name — the Dig
    // button on a row's own page — and pressing Research is press, press.
    const loaded = reportData as { dig?: BoardroomTableDig | null } | null;
    const onScreen = req.kind === 'table' && req.report === report ? loaded?.dig : null;
    if (onScreen && depth === 'DEEPER') {
      const who = [onScreen.who, ...(onScreen.also ?? [])].filter(Boolean).join(',');
      const ready = aimed(
        templateDig(
          onScreen.directive,
          'DEEPER',
          who ? { archetype: who, item_id: null, scope: 'table' } : null,
          'the table itself',
        ),
      );
      return { ...ready, plan: onScreen.hint };
    }

    // FIND MORE NEEDS NO WORDS AT ALL. There is no hole list for things nobody
    // has found, so there is nothing for a brief to route — the intent already
    // says "prefer untouched ground", and the scope already says where. On a
    // table the dispatcher staffs it from the catalogue, which is the same
    // narrowing a brief's seats were only ever approximating.
    // A ROW CARRIES ITS OWN DIG on its page — everything it lacks, as one
    // directive, computed from the same holes. No reason to write it again.
    const itemId = req.kind === 'item' ? req.itemId : null;
    // THE PAGE BEING LOADED IS WHAT MATTERS, not whether it has a dig.
    //
    // This gated on `profile.dig`, which is NULL for a row that lacks nothing —
    // so pressing Research on a company the room had finished fell through to
    // the brief and showed the per-seat prompts this window exists to retire.
    // A run aimed at an item needs the ITEM; the directive is optional steering.
    const loadedHere = itemId !== null && profile?.item?.id === itemId;
    const own = loadedHere ? profile?.dig ?? null : null;
    // The one case that still needs the slow path: a row whose page has not
    // loaded at all, where starting without its focus would aim at nothing.
    if (aimedAt && req.kind !== 'room' && !(onScreen && depth === 'DEEPER')) {
      const ready = aimed(
        loadedHere
          ? templateDig(
              own?.directive ?? '',
              depth,
              {
                archetype: [own?.who, ...(own?.also ?? [])].filter(Boolean).join(','),
                item_id: itemId,
                scope: 'item',
              },
              aimedAt.label,
            )
          : templateDig('', depth, null, aimedAt.label.toLowerCase()),
      );
      return {
        ...ready,
        scope:
          req.kind === 'lens'
            ? ({ tags: view.tags, verified: view.verified, by: view.by, sourced: view.sourced } as StagedDig['scope'])
            : ready.scope,
        plan:
          own?.hint ??
          (depth === 'WIDER'
            ? `Look for what ${aimedAt.label.toLowerCase()} has not got yet - the seats that fill it go and find more.`
            : `Work what is already in ${aimedAt.label.toLowerCase()} - read the pages, get the prices, find a second source.`),
      };
    }

    // NO WRITTEN BRIEFS. A dig used to start with a model call that read the
    // board and wrote each seat a job - for a table, a lens or a row. It only
    // ever steered the first round: the director rewrites every seat's
    // worklist each round after, from the same holes, and the run's scope
    // already keeps it to the table, lens or lead. So a Dig button starts
    // from its own template, at once, the way Research does.
    if (req.kind === 'room') {
      // Working the whole room is an intent and a budget - what the top
      // button always sent. No directive, and that is fine.
      dig = {
        ...aimed(templateDig('', depth, null, 'the whole room')),
        plan:
          depth === 'DEEPER'
            ? 'Everyone in the room works what is already on the board - reading pages, getting prices, finding second sources.'
            : 'Everyone in the room looks for what it has not got yet - new players, new complaints, new searches.',
      };
    } else {
      dig = aimed(fallback());
    }
    return dig;
  };

  // A DIG BUTTON IS THE DECISION. The dig is worked out on the way, with the
  // status bar saying so, and started - the same as Research. These buttons
  // (a table's Dig, "dig into this lens", an item's Dig) used to open a window
  // listing every prompt each seat would be sent, before anything ran: a
  // page nobody asked to read, standing between the click and the dig.
  const draftDig = async (
    req: DigRequest,
    title: string,
    fallback: () => StagedDig,
    depth: 'WIDER' | 'DEEPER' = 'DEEPER',
  ) => {
    setError(null);
    setPreparing(`Getting the dig ready: ${title}…`);
    try {
      const dig = await buildDig(req, fallback, depth);
      setPreparing('Starting the dig…');
      await startDig(dig, clampBudget(budget, maxBudget));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the dig');
    } finally {
      setPreparing(null);
    }
  };
  // THE ONE BUTTON. It opens the window that asks the two questions — what to
  // do and where — on "find more, the whole room" until the person picks
  // something else, then on what they picked last in this room.
  const aimKey = `${AIM_KEY}.${id}`;
  const lastAim = (): Aim | null => {
    try {
      const raw = window.localStorage.getItem(aimKey);
      return raw ? (JSON.parse(raw) as Aim) : null;
    } catch {
      return null; // private mode, or something unreadable: use the default
    }
  };
  // THE WINDOW ONLY ASKS. Opening it used to start writing a brief at once
  // and show every prompt it would send — a model call made before anyone
  // had decided to dig, and a page of prompts nobody asked to read. Now it
  // asks its two questions; pressing Research closes it and the dig is worked
  // out on the way, with the status bar saying so.
  const research = () => {
    const scopes = scopesFor(researchPlace);
    const aim = defaultAim(scopes, lastAim(), room?.next_dig?.depth ?? null);
    setDigModal({ title: 'Research', dig: null, ticket: digTicket.current, aim, scopes });
  };
  const reaim = (next: Aim) => {
    setDigModal(m => (m ? { ...m, aim: next } : m));
    try {
      window.localStorage.setItem(aimKey, JSON.stringify(next));
    } catch {
      /* nothing to remember it with */
    }
  };
  const [preparing, setPreparing] = useState<string | null>(null);
  const runResearch = async (aim: Aim, scopes: AimScope[]) => {
    setDigModal(null);
    setError(null);
    // ONE COMPANY'S SEARCHES: no dig. Find more adds searches about it; fill
    // the gaps searches the same ones again. The brand tab refreshes as they land.
    const target = scopeByKey(scopes, aim.scope);
    if (target.kind === 'brand' && target.itemId) {
      setPreparing(aim.depth === 'WIDER' ? 'Finding more searches about it…' : 'Searching again…');
      try {
        const out =
          aim.depth === 'WIDER'
            ? await api.widenBoardroomBrandSearches(id, target.itemId)
            : await api.startBoardroomBrandSearches(id, target.itemId);
        setNote(
          aim.depth === 'WIDER'
            ? out.added
              ? `Added ${out.added} searches about it - ${out.queries} in all; ${out.searches} being searched now.`
              : out.note || 'Nothing new to add yet.'
            : `${out.searches} searches running again; ${out.reused} recent ones kept.`,
        );
        setFacetsTick(t => t + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start the searches');
      } finally {
        setPreparing(null);
      }
      return;
    }
    setPreparing('Getting the dig ready…');
    try {
      const dig = await buildDig(
        requestFor(aim, scopes),
        () => templateDig('', aim.depth, null, 'the whole room'),
        aim.depth,
        scopeByKey(scopes, aim.scope),
      );
      setPreparing('Starting the dig…');
      await startDig(dig, clampBudget(budget, maxBudget));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the dig');
    } finally {
      setPreparing(null);
    }
  };

  const openProfile = (itemId: number) => go({ ...place, stage: 'profile', profileId: itemId });
  // A TAG ON A PAGE SETS THE LENS AND STAYS PUT. It used to open a table
  // narrowed to the tag, which took you off the page you were reading. The
  // lens narrows whatever on screen can be narrowed; a page about one item
  // has nothing to narrow, and the lens is there for the next table you open.
  const toggleLensTag = (key: string) => setView(v => ({ ...v, tags: toggleTag(v.tags, key) }));
  // THE TRAIL: where you have been, in order. Revisiting a crumb cuts the trail
  // back to it, as the browser's own history does.
  // It starts from the landing view even when you arrive on a profile by
  // link or reload, and it survives a reload for the life of the tab.
  const [trail, setTrail] = useState<Crumb[]>(() => loadTrail(id));
  // Another room, another trail: the page is not remounted when you switch.
  useEffect(() => {
    setTrail(loadTrail(id));
  }, [id]);
  useEffect(() => {
    const key = crumbKey(place);
    setTrail(t => {
      const next = stepTrail(
        t,
        {
          key,
          // The room's own name for a table ("Operators", not "Competitors").
          label:
            place.stage === 'profile'
              ? placeLabel(place, key === `profile:${profile?.item.id}` ? profile?.item.title : null)
              : visitName().label,
          search: location.search,
        },
        'board',
      );
      saveTrail(id, next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);
  useEffect(() => {
    if (!profile) return;
    const key = `profile:${profile.item.id}`;
    setTrail(t => t.map(c => (c.key === key ? { ...c, label: profile.item.title } : c)));
    // History learns its name too: the visit was logged before the page loaded.
    setHistory(h => {
      const next = nameVisits(h, key, profile.item.title, profile.item.kind.toLowerCase());
      if (next !== h) saveHistory(id, next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // HISTORY: every place opened in this room, newest first, kept across
  // visits (see `place.ts`). The trail above is a path back; this is a log.
  const [history, setHistory] = useState<Visit[]>(() => loadHistory(id));
  // The dig "see everything it found" asked Recon Runs to open (see `showRun`).
  // Up here with the other state: hooks below the loading return broke the page.
  const [focusRun, setFocusRun] = useState<{ runId: number; at: number } | null>(null);
  // AN OLD LINK TO THE RECON RUNS TAB opens it where it lives now, on the
  // rail. The address reader no longer knows the tab, so it lands on the
  // default table underneath.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('r') === 'recon_runs') panels.show('runs');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // WHAT THE RUNNING DIG HAS FOUND, counted on the server (`recon_runs.latest`).
  // It was counted from the feed panel's loaded rows: short when a filter was
  // on or past its 50, and the room's whole history after a reload mid-dig.
  const runningId = state?.room.run?.status === 'RUNNING' ? state.room.run.id : null;
  const [runFound, setRunFound] = useState<BoardroomReconRun | null>(null);
  useEffect(() => {
    if (!runningId) {
      setRunFound(null);
      return;
    }
    let alive = true;
    const load = () =>
      api
        .getBoardroomLatestDig(id, { runId: runningId })
        .then(r => {
          if (alive) setRunFound(r);
        })
        .catch(() => undefined);
    load();
    const t = window.setInterval(load, FOUND_REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [id, runningId]);
  useEffect(() => {
    setHistory(loadHistory(id));
  }, [id]);
  const visitName = (): { label: string; what: string } => {
    if (place.stage === 'profile') {
      const known = profile && profile.item.id === place.profileId;
      return {
        label: placeLabel(place, known ? profile!.item.title : null),
        what: known ? profile!.item.kind.toLowerCase() : 'item',
      };
    }
    // NAMED AS THE PAGE NAMES THEM, not by stage: "Board" and "reports" were
    // the code's words, and every open view was a row called "Board".
    if (place.stage === 'board') {
      return place.expanded
        ? { label: cardTitle(place.expanded) ?? 'A view', what: 'view' }
        : { label: 'Dashboard', what: 'dashboard' };
    }
    if (place.stage === 'reports')
      return { label: viewTitle(place.report, '', state?.room.room_kind) ?? placeLabel(place), what: 'table' };
    return { label: placeLabel(place), what: place.stage };
  };
  useEffect(() => {
    const { label, what } = visitName();
    setHistory(h => {
      const next = pushVisit(h, { at: Date.now(), key: visitKey(place), label, what, search: location.search });
      saveHistory(id, next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);

  const [facets, setFacets] = useState<BoardroomFacets | null>(null);
  const [facetsTick, setFacetsTick] = useState(0);
  // FEED & FILTERS HAS FILTERS OF ITS OWN. It used to read and write the
  // room's lens, so a tag clicked there narrowed every table, and a lens set
  // elsewhere quietly emptied the feed. Now it is narrowed only by what is
  // clicked in it, and nothing clicked in it reaches the rest of the room.
  // Kept on the page, not in the address: it is not a place you go back to.
  const [feedView, setFeedView] = useState<View>(EMPTY_VIEW);
  const [feedFacets, setFeedFacets] = useState<BoardroomFacets | null>(null);
  // ONLY WHAT IS WAITING TO BE SORTED: finds not on the axes yet, which no
  // lens can show. A feed filter like the rest, cleared with them.
  const [feedWaiting, setFeedWaiting] = useState<SortingWaitingPick | null>(null);
  const feedFiltered = viewIsSet(feedView) || !!feedWaiting;
  useEffect(() => {
    setFeedView(EMPTY_VIEW);
    setFeedFacets(null);
    setFeedWaiting(null);
  }, [id]);

  // SAVING THE SHAPE. A prompt rather than a dialog: the only thing that has
  // to be decided is what to call it, and everything else it will carry is
  // already visible on the page.
  const saveAsRecipe = useCallback(async () => {
    const name = window.prompt(
      'Save this room’s setup as a recipe.\n\nIt keeps the tables, the seats, the first questions and what this room compares on - never its findings.',
      state?.room?.name ? `${state.room.name} research` : 'My room setup',
    );
    if (!name?.trim()) return;
    try {
      const saved = await api.saveBoardroomAsRecipe(id, { name: name.trim() });
      setError(null);
      setNote(
        `Saved “${saved.name}” - ${saved.deliverables.length} tables, ${saved.seats.length} seats${
          saved.questions.length ? `, ${saved.questions.length} questions` : ''
        }. Pick it when you open your next room.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that recipe');
    }
  }, [id, state?.room?.name]);

  // DELETING THE ROOM. It is archived, not erased: it leaves the list of recons, and its
  // findings stay in the database.
  const deleteRoom = useCallback(async () => {
    const name = state?.room?.name ?? 'this recon';
    if (!window.confirm(`Delete "${name}"?\n\nIt will no longer show in your list of recons.`)) return;
    try {
      await api.deleteBoardroom(id);
      navigate('/boardroom');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete this recon');
    }
  }, [id, state?.room?.name, navigate]);

  // THE BOARD: what this room promises to hand over, and where its owner put
  // each one. Loaded once and re-fetched when a turn or a dig changes what is
  // in the tables — never per card, because every fill is a builder run over
  // the same graph.
  const [board, setBoard] = useState<BoardroomDeliverables | null>(null);
  /** A card on the board by its `kind:subject`, as its title. */
  // A ROOM'S OWN TABLE IS NAMED BY ITS VIEW ("Search · Demand"), as the
  // dashboard and the tabs name it; any other card by its own title.
  // UP HERE, NOT BY ITS FIRST USE BELOW: the visit history reads it after the
  // first render, and while the room loads the page returns before reaching
  // that line - reloading an address with a table open crashed the page.
  const cardTitle = (key: string | null | undefined): string | null => {
    if (!key) return null;
    const [kind, ...rest] = key.split(':');
    return viewTitle(kind, rest.join(':'), state?.room.room_kind) ?? board?.deliverables.find(d => `${d.kind}:${d.subject}` === key)?.title ?? null;
  };
  // WHICH PANEL IS UP OVER THE BOARD: Noticed, Observed or Add.
  const [boardPanel, setBoardPanel] = useState<BoardPanel | null>(null);
  // WHAT THEY HAVE SAID SO FAR, while the room is being opened: enough to rank
  // the saved shapes against before the room has a question of its own.
  const [recipeSubject, setRecipeSubject] = useState('');
  // The start form's first message, handed to the chat to send. A room opened
  // from another page (the Recent table's "Start a recon") brings it in `?ask=`.
  const [firstSay, setFirstSay] = useState<string | null>(() => new URLSearchParams(location.search).get('ask'));
  // Taken once, then dropped from the address, so a reload does not ask again.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!q.has('ask')) return;
    q.delete('ask');
    const rest = q.toString();
    navigate({ search: rest ? `?${rest}` : '' }, { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clearFirstSay = useCallback(() => setFirstSay(null), []);
  // Once the chat has a conversation, the form steps aside and the chat gets
  // the room: they are two ways to say the same first thing.
  const [conversing, setConversing] = useState(false);
  const markConversing = useCallback(() => setConversing(true), []);
  // Which CARD has the board ("kind:subject"), or null for the board itself.
  // Keyed on the card rather than the kind because a table and a cut of it are
  // two cards that would otherwise expand each other.
  // WHICH CARD IS OPEN ON THE BOARD lives in the address (`x`), so Back from
  // an item and a reload come back to it. Replace, never push: it is the same
  // place, looked at closely.
  const expandedKind = place.stage === 'board' ? place.expanded ?? null : null;
  // A TABLE OPENED THAT THE ROOM HAS NOT KEPT - from Recon runs, Find, or an
  // old link - is shown as it is, unsaved, with Save and Pin in its header.
  // Made from the catalogue, so it carries the room's own name for it.
  const temporaryView = useMemo((): BoardroomDeliverable | null => {
    if (!board || !expandedKind) return null;
    if (board.deliverables.some(d => d.status !== 'dropped' && `${d.kind}:${d.subject}` === expandedKind)) return null;
    const [kind, ...rest] = expandedKind.split(':');
    const entry = board.catalogue.find(c => c.kind === kind);
    if (!entry || rest.join(':')) return null;
    return {
      id: -1,
      kind,
      subject: '',
      lens: null,
      body: null,
      title: entry.title,
      answers: entry.answers,
      group: entry.group,
      entities: entry.entities,
      spec: {},
      active: false,
      pinned: false,
      position: 0,
      size: 'half',
      created_by: 'user',
      status: 'open',
      fill: { rows: 0, holes: 0, what: [] },
      preview: [],
      glance: null,
      summary: null,
      created_at: null,
      updated_at: null,
    } as BoardroomDeliverable;
  }, [board, expandedKind]);
  // Keep a temporary view: in All views only, or pinned.
  const keepTemporary = async (d: BoardroomDeliverable, pinned: boolean) => {
    try {
      setBoard(await api.addBoardroomDeliverable(id, { kind: d.kind, pinned }));
      setNote(pinned ? `${d.title} is on the Dashboard.` : `${d.title} is saved in All views.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that view');
    }
  };
  // A card the chat just built, waiting for the board to reload with it.
  const [cardToOpen, setCardToOpen] = useState<string | null>(null);
  // Pressed, and waiting on the server. Without it the button is pressable
  // twice and says nothing in between.
  const [starting, setStarting] = useState(false);
  const [whoRanks, setWhoRanks] = useState<BoardroomWhoRanks | null>(null);
  // A card waiting is worth a mark on the button: a person who does not open
  // the chat would otherwise never learn the room is stuck on something.
  const [decisionWaiting, setDecisionWaiting] = useState(false);
  // WHAT THE ROOM NOTICED. Derived, so it is simply fetched again whenever the
  // board moves; nothing is queued and nothing goes stale.
  const [notices, setNotices] = useState<BoardroomNotice[]>([]);
  // THE ROOM'S TEXT FILTER (top bar). Kept while you move around the room;
  // another room starts clear.
  const [tableFilter, setTableFilter] = useState('');
  // THE FILTER'S COUNTS PER CARD, over each card's whole table: a card holds
  // only its first rows, so counting those said "no match" for a word in row
  // 21 or in a later column. Asked once typing pauses, and again when the
  // board changes; a reply for older text is not shown.
  const [matchReply, setMatchReply] = useState<{ q: string; matches: Record<string, { hits: number; rows: number }> } | null>(null);
  useEffect(() => setTableFilter(''), [id]);
  useEffect(() => {
    const q = tableFilter.trim();
    if (!filterWords(q).length) {
      setMatchReply(null);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      api
        .getBoardroomDeliverableMatches(id, q, boardLens.current.key ? boardLens.current.lens : {})
        .then(r => {
          if (alive) setMatchReply(r);
        })
        .catch(() => {
          /* no counts is the honest fallback: the cards just show no number */
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [id, tableFilter, board]);
  const boardMatches = useMemo(() => {
    if (!matchReply || matchReply.q !== tableFilter.trim()) return null;
    const out: Record<number, { hits: number; seen: number }> = {};
    for (const [k, v] of Object.entries(matchReply.matches)) out[Number(k)] = { hits: v.hits, seen: v.rows };
    return out;
  }, [matchReply, tableFilter]);
  // What was set aside as not interesting, the one just set aside (for its
  // Undo), and the one being looked at before deciding.
  const [dismissedNotices, setDismissedNotices] = useState<BoardroomDismissedNotice[]>([]);
  const [undoNotice, setUndoNotice] = useState<BoardroomNotice | null>(null);
  const [previewNotice, setPreviewNotice] = useState<BoardroomNotice | null>(null);
  useEffect(() => {
    if (!undoNotice) return;
    const t = window.setTimeout(() => setUndoNotice(null), 10_000);
    return () => window.clearTimeout(t);
  }, [undoNotice]);
  const [noticeBusy, setNoticeBusy] = useState(false);
  // WHAT THE OBSERVER NOTICED looking over the whole board. Items, so they
  // arrive as item events too; fetched again when one does.
  const [observations, setObservations] = useState<BoardroomObservation[]>([]);
  const [observationBusy, setObservationBusy] = useState(false);
  const loadObservations = useCallback(async () => {
    try {
      setObservations((await api.getBoardroomObservations(id)).observations);
    } catch {
      /* a tray nobody can draw is simply empty */
    }
  }, [id]);
  const markObservationSeen = useCallback(
    async (o: BoardroomObservation) => {
      setObservationBusy(true);
      try {
        await api.updateBoardroomItem(id, o.id, { status: 'DONE' });
        setObservations(list => list.filter(x => x.id !== o.id));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not do that');
      } finally {
        setObservationBusy(false);
      }
    },
    [id],
  );
  // THE DASHBOARD IS DRAWN INSIDE THE ROOM'S LENS, like every table: a tag
  // picked in the Narrow drawer narrows the cards too. The lens is read from a
  // ref so `loadBoard` keeps one identity; `lensKey` says which lens a board
  // was drawn in.
  const roomLens = lensOf(view);
  const lensKey =
    roomLens.tags.length || roomLens.verified || roomLens.sourced || roomLens.by
      ? JSON.stringify([[...roomLens.tags].sort(), roomLens.verified ?? null, roomLens.sourced ?? null, roomLens.by ?? null])
      : '';
  const boardLens = useRef<{ lens: Lens; key: string }>({ lens: roomLens, key: lensKey });
  // HOW MANY ITEMS THE LENS LEAVES, said at the end of its sentence: the
  // facet counts are taken under the same lens.
  const lensTotal = lensKey && facets ? Object.values(facets.item_counts ?? {}).reduce((a, b) => a + b, 0) : null;
  boardLens.current = { lens: roomLens, key: lensKey };
  const loadBoard = useCallback(async () => {
    try {
      const { lens, key } = boardLens.current;
      setBoard(await api.getBoardroomDeliverables(id, key ? lens : {}, key || undefined));
    } catch {
      /* the board is a view; a failed fetch leaves the last one up */
    }
    try {
      const tray = await api.getBoardroomNotices(id);
      setNotices(tray.notices);
      setDismissedNotices(tray.dismissed ?? []);
    } catch {
      /* a tray nobody can draw is simply empty */
    }
    loadObservations();
  }, [id, loadObservations]);
  const answerNotice = useCallback(
    async (notice: Pick<BoardroomNotice, 'key'> & Partial<BoardroomNotice>, choice: 'take' | 'dismiss' | 'restore') => {
      setNoticeBusy(true);
      try {
        const out = await api.answerBoardroomNotice(id, { key: notice.key, choice });
        setNotices(out.notices);
        if (out.dismissed) setDismissedNotices(out.dismissed);
        setUndoNotice(choice === 'dismiss' && notice.title ? (notice as BoardroomNotice) : null);
        if (choice !== 'restore') setPreviewNotice(null);
        // Taking one puts a card on the board, so the board is what changed.
        if (out.took) loadBoard();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not do that');
      } finally {
        setNoticeBusy(false);
      }
    },
    [id, loadBoard],
  );
  useEffect(() => {
    loadBoard();
  }, [loadBoard]);
  // A BOARD DRAWN IN ANOTHER LENS - a live push, an edit's reply, or the lens
  // just changed - is asked for again in this one.
  useEffect(() => {
    if (board && (board.lens_key ?? '') !== lensKey) loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, lensKey]);

  useEffect(() => {
    let alive = true;
    api
      .getBoardroomDecision(id)
      .then(r => {
        if (alive) setDecisionWaiting(Boolean(r.decision));
      })
      .catch(() => {
        /* a missing card is the common case */
      });
    return () => {
      alive = false;
    };
  }, [id, board]);
  // ONLY THE NEWEST ANSWER MAY WRITE THE BOARD.
  //
  // Every PATCH returns the WHOLE block, so two clicks in quick succession race:
  // the first response was computed before the second click existed, and
  // whichever lands last wins. Pinning A then B, B's change was undone by A's
  // late reply — the slideout's button had already flipped, so it read as "the
  // button toggles but the board does not". A ticket, not a queue: a stale
  // reply is dropped, and the newest request is already on its way with the
  // truth.
  const patchSeq = useRef(0);
  const patchDeliverable = useCallback(
    async (deliverableId: number, body: Parameters<typeof api.patchBoardroomDeliverable>[2]) => {
      const seq = ++patchSeq.current;
      // Optimistic: pinning is a view change and must feel like one.
      setBoard(b =>
        b
          ? {
              ...b,
              deliverables: b.deliverables.map(d =>
                d.id === deliverableId ? { ...d, ...body } : d,
              ),
            }
          : b,
      );
      try {
        const fresh = await api.patchBoardroomDeliverable(id, deliverableId, body);
        if (seq === patchSeq.current) setBoard(fresh);
      } catch {
        if (seq === patchSeq.current) loadBoard();
      }
    },
    [id, loadBoard],
  );

  // ON THE LIST OR OFF IT. The list has no room to run out of, so a pin
  // always lands where you can see it.
  const pinCard = useCallback(
    (d: BoardroomDeliverable, pinned: boolean, extra: { active?: boolean } = {}) =>
      patchDeliverable(d.id, pinned ? { pinned: true, ...extra } : { pinned: false }),
    [patchDeliverable],
  );

  const findCard = (kind: string, subject: string) =>
    board?.deliverables.find(d => d.kind === kind && d.subject === subject) ?? null;
  const deleteCard = async (d: BoardroomDeliverable) => {
    const again = d.kind === 'list' || d.kind === 'report' ? ' The room chat can build it again if you ask.' : '';
    if (!window.confirm(`Delete "${d.title}" for good?${again}`)) return;
    try {
      // FOR GOOD. Unpinning keeps it in All views to pin again; Delete is the
      // one that removes it.
      setBoard(await api.dropBoardroomDeliverable(id, d.id, true));
      // AND OFF THE BACK TRAIL: Back would open a view that is gone.
      const gone = `board:${d.kind}:${d.subject}`;
      setTrail(t => {
        const next = t.filter(c => c.key !== gone);
        saveTrail(id, next);
        return next;
      });
      // The view you were reading is gone: back to the Dashboard.
      if (place.expanded === `${d.kind}:${d.subject}`) go({ ...place, stage: 'board', expanded: null }, true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete that');
    }
  };

  // A line the board wants said to the room, dropped into the composer rather
  // than sent: "go get these" is an ask a person should be able to qualify.
  const [chatDraft, setChatDraft] = useState<string | null>(null);
  // COMPARE, slid out over the board: companies side by side.
  // COMPARE IS IN THE ADDRESS (`?compare=12,34`), so a comparison can be
  // linked and reopened. Beside the place, not part of it: moving on closes it.
  const compareParam = new URLSearchParams(location.search).get('compare');
  const compareOpen = compareParam !== null;
  const setCompare = (ids: number[] | null, replace = false) => {
    const q = new URLSearchParams(location.search);
    if (ids === null) q.delete('compare');
    else q.set('compare', ids.join(','));
    navigate({ search: q.toString() }, { replace, state: location.state });
  };
  const openCompare = (withId?: number) => {
    const picks = storedPicks(id);
    setCompare(withId ? withFirst(picks, withId) : picks);
  };
  // What a chat turn touched. Anything it changed about the ROOM (its question,
  // its frame, its seats, a dig it started) is refetched once; findings and run
  // progress already arrive on the socket, so this never repaints for those.
  // FIND searches items on the server, newest first. Stable, so the box's
  // pause-before-searching is not restarted by every live update.
  // The Tags panel's list, read when it opens. Stable, so opening it once
  // reads it once.
  const loadTags = useCallback(() => api.getBoardroomTags(id), [id]);
  const searchItems = useCallback(
    (q: string) => api.listBoardroomItems(id, { q, limit: 8 }).then(page => [...page.items].reverse()),
    [id],
  );
  const onChatChanged = useCallback(
    async (changed: BoardroomChatChanged) => {
      const touched =
        changed.room ||
        changed.frame ||
        changed.seats ||
        changed.questions > 0 ||
        changed.tables ||
        changed.started_run_id > 0 ||
        changed.stopped_run_id > 0;
      if (!touched) return;
      if (changed.open_card) setCardToOpen(changed.open_card);
      try {
        const fresh = await api.getBoardroom(id);
        setState(s => (s ? { ...s, room: { ...s.room, ...fresh } } : s));
      } catch {
        /* the socket will catch us up */
      }
      // A turn that changed the room's words changes what its tables are
      // CALLED — a room that starts saying "brands" wants its competitor table
      // headed that way.
      if (changed.room || changed.frame || changed.tables) loadBoard();
    },
    [id, loadBoard],
  );

  // OPEN WHAT THE CHAT JUST BUILT, on its own tab in Reports. The chat used to
  // say "it's on the board" about a card the person had no way to find. Once
  // the board has reloaded with it, its tab exists, so open that.
  useEffect(() => {
    if (!cardToOpen || !board) return;
    const card = board.deliverables.find(d => `${d.kind}:${d.subject}` === cardToOpen);
    if (!card) return;
    setCardToOpen(null);
    if (isCardKind(card.kind) || isCut(card)) openCard(card.kind, card.subject, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardToOpen, board]);

  // WHO RANKS IS FETCHED, not derived from the graph like the other tables —
  // it counts SERP entries, which the room graph deliberately does not carry.
  // Once, on the board, because it is the one card whose preview is its rows.
  useEffect(() => {
    let alive = true;
    // Only when the room has actually chosen it.
    if (!board?.deliverables.some(d => d.kind === 'who_ranks' && d.status !== 'dropped')) return;
    api
      .getBoardroomReport(id, 'who_ranks' as BoardroomReportKey)
      .then(r => {
        if (alive) setWhoRanks(r as unknown as BoardroomWhoRanks);
      })
      .catch(() => {
        /* an empty card says "nothing yet", which is true */
      });
    return () => {
      alive = false;
    };
  }, [id, board]);

  // WHAT A CARD SHOWS AT CARD SIZE. The full table is the report behind
  // "open"; this is the few lines that say whether it is worth opening —
  // which, for a table with holes, is WHAT it is short of rather than a
  // truncated copy of rows the reader cannot act on.
  const renderDeliverableCard = useCallback(
    (d: BoardroomDeliverable, lines = 5) => {
      // A NOTE IS ITS TEXT. Every other card previews rows it stands for; this
      // one has no rows — what a person concluded is the whole of it.
      if (d.kind === 'note') {
        return (
          <div className="prose prose-sm max-w-none text-xs text-gray-700 dark:prose-invert dark:text-gray-200">
            <ChatMarkdown text={d.body || '_Empty note._'} />
          </div>
        );
      }
      if (d.kind === 'who_ranks') {
        return whoRanks ? <WhoRanksReport data={whoRanks} limit={lines} /> : null;
      }
      // THE TABLE'S OWN ROWS. What it is short of goes under them in one line —
      // a card that leads with its gaps is a to-do list, and the board is meant
      // to be the findings. As many as the board has room for (`lines`).
      // WITH A FILTER ON, only the rows that mention it, so a card shows where
      // the word turns up rather than the same top rows with a count beside.
      const words = filterWords(tableFilter);
      const shown = (d.preview ?? [])
        .filter(r => textMatches(previewText(r), words))
        .slice(0, lines);
      if (words.length && !shown.length) {
        // The first rows had none; the count over the whole table says whether
        // any are further down.
        const m = boardMatches?.[d.id];
        return (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {!boardMatches
              ? 'Checking the rest of the table…'
              : m && m.hits
                ? `${m.hits} further down mention “${tableFilter.trim()}”. Open the table to see them.`
                : `Nothing here mentions “${tableFilter.trim()}”.`}
          </p>
        );
      }
      return (
        <div className="space-y-1">
          <ul className="space-y-0.5 text-xs text-gray-700 dark:text-gray-200">
            {shown.map(r => (
              <li key={r.title} className="flex gap-2">
                <span className="min-w-0 flex-1 truncate">{r.title}</span>
                {r.note && (
                  <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                    {r.note}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {/* The rest of the table is not counted against the filter here,
              so "+N more" would be a guess; the card's head says how many match. */}
          {!words.length && d.fill.rows > shown.length && (
            <div className="text-xs text-gray-400 dark:text-gray-500">
              +{d.fill.rows - shown.length} more
              {d.fill.holes
                ? ` · ${d.fill.holes} gaps: ${(d.fill.words ?? d.fill.what).slice(0, 2).join(', ')}`
                : ''}
            </div>
          )}
          {!words.length && d.fill.rows <= shown.length && d.fill.holes > 0 && (
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {d.fill.holes} gaps: {(d.fill.words ?? d.fill.what).slice(0, 2).join(', ')}
            </div>
          )}
        </div>
      );
    },
    [whoRanks, tableFilter, boardMatches],
  );

  // THE REAL TABLE, inside the expanded card — the same components the reports
  // view renders, so a table looks the same wherever it is read. It reads the
  // page's own report data, which `onExpand` pointed at this kind.
  //
  // A PLAIN FUNCTION, not a cached one: it reads values declared further down
  // the page (the column undo, the split, the live tick), and the dependency
  // list of a cached one would read them before they exist.
  const renderDeliverableFull = (d: BoardroomDeliverable) => {
      // What the chat's own tables refetch on, so they keep pace with findings.
      const cardRefresh = `${liveItemId}:${facetsTick}`;
      if (d.kind === 'who_ranks') {
        return whoRanks ? <WhoRanksReport data={whoRanks} /> : null;
      }
      if (d.kind === 'note') {
        return (
          <div className="prose max-w-none text-sm text-gray-800 dark:prose-invert dark:text-gray-100">
            <ChatMarkdown text={d.body || '_Empty note._'} />
          </div>
        );
      }
      // ONE THING IN FULL. Same reason as `list`: the subject says which item,
      // and the page holds one report per kind.
      if (d.kind === 'profile') {
        return (
          <ProfilePane
            boardroomId={id}
            subject={d.subject}
            onOpen={openProfile}
            onEdit={openById}
            onDig={(directive, intent, focus) => stageDig(directive, intent, focus, 'a Dig button')}
            digging={room.run?.status === 'RUNNING'}
          />
        );
      }
      // ONE COMPANY'S SEARCHES, fetched by the company id for the same reason.
      if (d.kind === 'brand') {
        return (
          <BrandPane boardroomId={id} subject={d.subject} onOpen={openById} roomName={room.name} refreshKey={cardRefresh} />
        );
      }
      // A TABLE THE ROOM WAS ASKED FOR fetches its own rows: the page holds one
      // report per kind, and two `list` cards share that kind. Its subject is
      // what tells them apart.
      if (d.kind === 'list') {
        return (
          <ListCardPane
            boardroomId={id}
            subject={d.subject}
            tags={view.tags}
            verified={view.verified}
            onOpen={openById}
            onPickTag={k => setView(v => ({ ...v, tags: toggleTag(v.tags, k) }))}
            refreshKey={cardRefresh}
          />
        );
      }
      // A REPORT: several blocks, fetched by subject for the same reason.
      if (d.kind === 'report') {
        return (
          <ReportCardPane
            boardroomId={id}
            subject={d.subject}
            tags={view.tags}
            verified={view.verified}
            onOpen={openById}
            onPickTag={k => setView(v => ({ ...v, tags: toggleTag(v.tags, k) }))}
            refreshKey={cardRefresh}
          />
        );
      }
      const key = d.kind as BoardroomReportKey;
      return (
        <Reports
          onOpenTable={k => setReport(k)}
          report={key}
          lastDig={
            <LastDigSummary boardroomId={id} table={key} refreshKey={reportData} onShowRun={showRun} />
          }
          // About the whole table, so it steps aside while a filter narrows it.
          summary={
            // THE TABLE AS ITS PARAGRAPH DESCRIBES IT: the whole room's, or the
            // followed lead's - not a lens narrowed past either.
            d.summary && !narrowsPast(view, room.lead) ? (
              <TableSummary
                summary={d.summary}
                onRewrite={() => api.rewriteBoardroomSummary(id, d.id)}
                links={nameLinks(reportData)}
                // Deferred: `openById` is declared further down this component.
                onOpen={itemId => openById(itemId)}
              />
            ) : null
          }
          loadFindings={runId => api.getBoardroomRunFindings(id, runId)}
          focusRun={focusRun}
          roomName={state?.room.name ?? ''}
          boardroomId={id}
          canPublish={has('PUBLISH')}
          view={view}
          onView={setView}
          data={reportData}
          loading={reportLoading}
          onOpen={openById}
          // A TABLE'S DIG is written as a brief; a dig into one row stays the line it was.
          onDig={(directive, intent, focus) =>
            focus?.scope === 'table'
              ? draftDig({ kind: 'table', report: key }, `the ${d.title.toLowerCase()} table`, () =>
                  templateDig(directive, intent, focus, 'a Dig button'),
                )
              : stageDig(directive, intent, focus, 'a Dig button')
          }
          digging={state?.room.run?.status === 'RUNNING'}
          unseen={buckets[key as keyof Buckets]}
          onSeen={ids => setBuckets(b => markSeen(b, key, ids))}
          onCrossBy={setCrossBy}
          onPickTag={k => setView(v => ({ ...v, tags: toggleTag(v.tags, k) }))}
          // IS THE NEXT DIG WORKING ON THIS TABLE? Only for the room's own,
          // saved table that the room can fill.
          // SORTING KINDS IS A ROOM-WIDE CHANGE. One answer moves everything
          // filed under that kind on and off every table and out of the work
          // queue, so the whole page is refetched rather than one report.
          onSetRelevance={async (itemId, relevance) => {
            setSortingKinds(true);
            try {
              await api.setBoardroomRelevance(id, itemId, relevance);
              setFacetsTick(t => t + 1);
              loadBoard();
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Could not sort that kind');
            } finally {
              setSortingKinds(false);
            }
          }}
          sortingKinds={sortingKinds}
          columnChange={columnChange(room.frame_history)}
          onUndoColumns={onUndoColumns}
          onSplitType={onSplitType}
          onUndoSplit={onUndoSplit}
          splittingType={splittingType !== null}
        />
      );
  };

  // THE TABLE THE READER IS ON, and how short it is.
  //
  // REPORTED EVEN WHEN IT IS FULL. This used to answer null for a table with no
  // holes, on the reasoning that offering to FILL a full table spends money to
  // discover there was nothing to do — but that dropped the table out of the
  // Research window entirely, so a dig that closed every hole took away the
  // only way to ask for more of the same table. Room 30's price map went to
  // `missing: 0` and the option vanished mid-session.
  //
  // Emptiness belongs one level down: `scopesFor` still offers a full table
  // ("nothing missing on it"), and the window opens on "find more" rather than
  // "fill the gaps" for it. The count is the table's own — the same number its
  // Dig button shows — so the two can never disagree.
  // THE CARD OPEN ON THE BOARD is where the reader is, as much as a page is.
  // Research offered only "the whole room" with a pain open on the board, so
  // pressing it sent every seat across the room instead of at the pain.
  const cardOnBoard = useMemo(
    () => (expandedKind ? (board?.deliverables ?? []).find(d => `${d.kind}:${d.subject}` === expandedKind) ?? null : null),
    [expandedKind, board],
  );
  const tableOnBoard =
    stage === 'board' && !!cardOnBoard && cardOnBoard.kind === report && (cardOnBoard.subject || '') === (place.subject ?? '');
  const tableBeingRead = useMemo(() => {
    // A chat-built card has no holes, and `reportData` is the last built-in table.
    if (!tableOnBoard || !reportData || isCardKind(report)) return null;
    // A TABLE NO SEAT CAN WORK ON (email capture, who ranks): it reads what is
    // already fetched, so a dig aimed at it would have nobody to do it. The
    // window offers the room instead.
    if (board?.catalogue.find(c => c.kind === report)?.workable === false) return null;
    // `who_ranks` and the list cards are not short of anything — they read what
    // is already there — so the union has no `dig`/`missing` on every arm.
    const d = reportData as { dig?: { count: number } | null; missing?: unknown[] };
    const holes = d.dig?.count ?? (d.missing ?? []).length;
    const own = (board?.deliverables ?? []).find(
      d => d.kind === report && !d.subject && d.status !== 'dropped',
    );
    const label =
      (tableOnBoard && cardOnBoard?.subject ? cardOnBoard.title : null) ||
      own?.title ||
      REPORT_TABS.find(t => t.key === report)?.label?.toString() ||
      'this table';
    return { key: report, label, holes };
  }, [report, reportData, board, tableOnBoard, cardOnBoard]);

  // WHERE THE READER IS, as the window's options. Everything it needs is
  // already on screen — which table, which row, which lens, and how short each
  // table is — so the choices describe the place rather than a fixed menu.
  const researchPlace = useMemo(
    () => ({
      stage,
      table: tableBeingRead,
      item:
        stage === 'profile' && profile?.item
          ? { id: profile.item.id, title: profile.item.title }
          : cardOnBoard?.kind === 'profile' && Number(cardOnBoard.subject) > 0
            ? { id: Number(cardOnBoard.subject), title: cardOnBoard.title }
            : null,
      // A BRAND REPORT ON SCREEN: its company's searches are somewhere to aim.
      brand: (() => {
        const subject = cardOnBoard?.kind === 'brand' ? cardOnBoard.subject : null;
        if (!subject) return null;
        const card = (board?.deliverables ?? []).find(d => d.kind === 'brand' && d.subject === subject);
        if (!card) return null;
        const title = card.title.includes(' · ') ? card.title.split(' · ').slice(1).join(' · ') : card.title;
        return { id: Number(subject), title, searches: card.fill?.rows ?? 0 };
      })(),
      // The lens as the reports' own words for it, so the option reads the same
      // as the chips it refers to. Following a lead, only a lens narrowed past
      // it is somewhere new to aim: the lead's own chips are "All of" it.
      lensLabel: narrowsPast(view, state?.room.lead)
        ? scopeLabel({
            tags: view.tags,
            verified: view.verified,
            by: view.by,
          })
        : '',
      lead: state?.room.lead?.title ?? null,
      tables: (board?.deliverables ?? [])
        .filter(d => !d.subject && d.status !== 'dropped')
        .map(d => ({ holes: d.fill.holes })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stage, report, place.subject, tableBeingRead, profile, view, board, cardOnBoard, state?.room.lead],
  );

  // "LANDED SINCE YOU LOOKED", per table. The bucket tallies already map an
  // item kind to the reports it is a row of; a deliverable wears its own.
  const deliverableBadges = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [key, ids] of Object.entries(buckets)) {
      const n = (ids as Set<number> | undefined)?.size ?? 0;
      if (n) out[key] = n;
    }
    return out;
  }, [buckets]);

  // A CARD CAN GO STALE UNDER YOUR HAND. A room with a skeptic ratifies its
  // own proposals, so the change a button offers may already be settled by the
  // time it is clicked. That is a race, not a mistake — say what happened and
  // refresh, rather than letting a rejected promise become a red overlay.
  const onFrame = useCallback(
    async (run: () => Promise<Partial<BoardroomDetail>>) => {
      try {
        const res = await run();
        setState(s => (s ? { ...s, room: { ...s.room, ...res } } : s));
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not change the setup');
        try {
          const fresh = await api.getBoardroom(id);
          setState(s => (s ? { ...s, room: { ...s.room, ...fresh } } : s));
        } catch {
          /* the message stands on its own */
        }
      }
    },
    [id],
  );

  // THE ROOM CHANGES ITS OWN COLUMNS (see reports/columnChange.ts); a person
  // only ever needs the way back.
  const onUndoColumns = useCallback(
    async (frameId: number) => {
      try {
        const block = await api.undoBoardroomColumns(id, frameId);
        setState(s => (s ? { ...s, room: { ...s.room, ...block } } : s));
        setError(null);
        setFacetsTick(t => t + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not put the columns back');
      }
    },
    [id],
  );

  // A TYPE DOING THE WORK OF SEVERAL MARKETS. Additive and narrow: it names
  // the kinds, files them under the broad type so that name stays, and moves
  // only the products nobody had sorted. What it did arrives as items over the
  // socket, so there is nothing to poll.
  //
  // WHICH type is being split, not just whether: the pass announces each kind
  // it names as an item, and a dig streams items of its own, so "any item"
  // cleared this within moments and a second click queued a second split. The
  // pass always ends by publishing the type it split, whatever happened.
  const [splittingType, setSplittingType] = useState<number | null>(null);
  const onSplitType = useCallback(
    async (typeId: number) => {
      setSplittingType(typeId);
      try {
        await api.startBoardroomSplitType(id, typeId);
        setError(null);
      } catch (e) {
        setSplittingType(null);
        setError(e instanceof Error ? e.message : 'Could not split the type');
      }
    },
    [id],
  );

  // THE WAY BACK. A sorting is a judgement, and the only way to judge one is
  // to see it on the board — so it has to be possible to put it back and ask
  // again. No queue: it is a handful of link deletes, and somebody waiting to
  // see their board go back should not be watching a spinner.
  const onUndoSplit = useCallback(
    async (typeId: number) => {
      try {
        await api.undoBoardroomSplitType(id, typeId);
        setError(null);
        setFacetsTick(t => t + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not put the split back');
      }
    },
    [id],
  );

  const personas = useMemo(() => {
    const m = new Map<number, BoardroomPersona>();
    state?.room.personas.forEach(p => m.set(p.id, p));
    return m;
  }, [state?.room.personas]);

  // The room, once; the feed, per view.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const room = await api.getBoardroom(id);
        if (alive) setState({ room, items: [] });
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Could not load the recon');
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const loadItems = useCallback(
    // BACKWARDS. The feed reads newest first, so the first page is the last
    // fifty things that happened and "load more" walks further into the past.
    // `before` is the OLDEST row already shown.
    async (before: number | null) => {
      setLoadingItems(true);
      try {
        // The feed's own filters, never the room's lens (see `feedView`).
        const page = await api.listBoardroomItems(id, {
          filter: feedView.filter,
          tags: feedView.tags,
          verified: feedView.verified,
          sourced: feedView.sourced,
          by: feedView.by,
          lookup: feedView.lookup,
          // A kind picked under a sorting queue narrows by kind too.
          kind: feedWaiting?.kind ?? feedView.kind,
          waiting: feedWaiting?.queue ?? null,
          before_id: before,
          limit: PAGE,
        });
        setState(s => {
          if (!s) return s;
          const merged = before ? page.items.reduce((acc, it) => upsertItem(acc, it), s.items) : page.items;
          return { ...s, items: merged };
        });
        setHasMore(page.next_before_id !== null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load items');
      } finally {
        setLoadingItems(false);
      }
    },
    [id, feedView, feedWaiting],
  );

  useEffect(() => {
    if (state) loadItems(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadItems, state?.room.id]);

  useEffect(() => {
    return subscribe(event => {
      // THIS ROOM'S EVENTS ONLY. They arrive on the user's channel, so a dig in
      // another room reaches this page too; `applyEvent` ignored it, but the
      // unread tallies, the last run, the facet refetch and the columns
      // spinner below did not.
      if (event.event_type !== 'boardroom' || event.boardroom_id !== id) return;
      if (event.status === 'run' && event.run && event.run.status !== 'RUNNING') {
        setLastRun(event.run);
        // WHAT IS LEFT TO PROCESS, read again once the dig is over: its last
        // turns mark their results read after the events that would refetch,
        // and the button offered "Process 278 results" over the 31 left.
        setFacetsTick(t => t + 1);
      }
      setState(s => (s ? applyEvent(s, event, feedView, !feedWaiting) : s));
      // WHAT LANDED IN A TABLE YOU ARE NOT LOOKING AT. Every item a dig
      // produces already arrives here — the feed is built from it — so the
      // count costs nothing beyond a tally. The report on screen never counts:
      // you are looking at it.
      setBuckets(b => bucket(b, event));
      setFreshState(f => (f.room === id ? { ...f, ids: noteFresh(f.ids, event) } : f));
      // A lookup changing state (queued → fetching → landed → read) keeps its
      // id, so the cursor-driven facet refetch would miss it; ask again. A
      // result is marked READ at the end of a turn with no item event of its
      // own, so a persona event (turn start / turn end) refetches too — the
      // waiting count then falls turn by turn instead of in a lump.
      // A TAG EVENT MOVES THE FACETS TOO, and until the sorter existed there
      // were few enough of them per turn that the persona refetch covered it.
      // A loop placing ten at a time makes the axis counts drift all dig.
      if (
        (event.status === 'item' && event.item && LOOKUP_KINDS.includes(event.item.kind)) ||
        event.status === 'persona' ||
        event.status === 'tag'
      ) {
        setLiveBump(t => t + 1);
      }
      // THE SORTER'S AND APPEAL'S OWN COUNTS, straight into what the panel draws.
      // It draws the counts it fetched, so an event that only changed the room's
      // copy moved nothing until the next refetch - the Sorting line sat still
      // while the sorter worked.
      if (event.status === 'tagging' && event.tagging) {
        const tagging = event.tagging;
        setFacets(f => (f ? { ...f, tagging } : f));
      }
      if (event.status === 'appeal' && event.appeal) {
        const appeal = event.appeal;
        setFacets(f => (f ? { ...f, appeal } : f));
        // A feed narrowed to an Appeal row ("reading") is reloaded on the tick,
        // so a read that finishes leaves it.
        setLiveBump(t => t + 1);
      }
      // The split is over when the type it split is published again.
      if (event.status === 'item' && event.item) {
        const itemId = event.item.id;
        setSplittingType(t => (t === itemId ? null : t));
        if (event.item.kind === 'OBSERVATION') loadObservations();
      }
      // The columns landed: the pass is over, however it went. A frame event
      // is the only thing it produces, so this is the whole "is it still
      // going" story — no polling, and nothing to leave spinning if the job
      // dies, because the next frame event of any kind clears it too.
      if (event.status === 'frame') {
        // The matrix reads the facts the fitting writes, so ask for it again.
        setLiveBump(t => t + 1);
      }
      // A table's "What this means" was written (or could not be).
      if (event.status === 'summary') loadBoard();
      // THE DASHBOARD AS THE SERVER REBUILT IT. Its numbers are worked out
      // from the whole room, so an item event cannot move them; the server
      // sends the lot once for the turns ending around the same time, and
      // at once when a dig ends (`board_push`). Nothing is fetched.
      if (event.status === 'board' && event.board) {
        const { notices: tray, dismissed, observations: seen, ...cards } = event.board;
        setBoard(cards);
        if (tray) setNotices(tray);
        if (dismissed) setDismissedNotices(dismissed);
        if (seen) setObservations(seen);
      }
    });
  }, [subscribe, feedView, feedWaiting, id, setBuckets, loadObservations, loadBoard]);

  // NEW ITEMS REFRESH AT MOST EVERY 10s, and not while the table is still
  // loading (see useLiveTick). A change you make — a lens, a table — still
  // fetches at once.
  //
  // LIVE EVENTS GO THROUGH THE SAME GATE. A lookup changing state, a seat
  // starting or ending a turn, and every tag the sorter places used to refetch
  // the counts and the open table at once: in one dig on room 41 that was 343
  // counts and 135 feature-matrix requests in five minutes, all queued for the
  // API's five database connections, so a click waited behind a minute of
  // them. They bump `liveBump`, which is slowed like the item cursor; what a
  // person does still bumps `facetsTick` and fetches at once.
  const [liveBump, setLiveBump] = useState(0);
  const [countsLoading, setCountsLoading] = useState(false);
  const liveItemId = useLiveTick(`${state?.room.last_item_id ?? 0}:${liveBump}`, reportLoading || countsLoading);

  // FACETED COUNTS. Every axis in the rail is counted under the other two, so a
  // number is what clicking it would show. Re-read on any view change and
  // whenever the board grows; the projection's totals stay for "x of y".
  useEffect(() => {
    if (!state) return;
    let alive = true;
    setCountsLoading(true);
    api
      .getBoardroomCounts(id, { tags: view.tags, verified: view.verified, sourced: view.sourced, by: view.by })
      .then(c => {
        if (!alive) return;
        setFacets(c);
        // The room's lookup totals are only fetched with the detail, once. A
        // result being read changes an item's state, not its id, so nothing
        // else refreshes them; carry the fresh numbers over whenever this
        // fetch is unnarrowed (an author lens would make them partial).
        if (!view.by) {
          setState(s =>
            s
              ? {
                  ...s,
                  room: {
                    ...s.room,
                    lookup_counts: c.lookup_counts,
                    lookup_stages: c.lookup_stages ?? s.room.lookup_stages,
                    to_process: c.to_process ?? s.room.to_process,
                  },
                }
              : s,
          );
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setCountsLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, view.tags, view.verified, view.sourced, view.by, liveItemId, facetsTick]);

  // THE FEED'S OWN COUNTS, under its own filters, and only while the panel is
  // out: every count is a query, and nobody reads these with it closed.
  const feedOpen = panels.docked === 'feed' || panels.overlay === 'feed';
  useEffect(() => {
    if (!state || !feedOpen) return;
    let alive = true;
    api
      .getBoardroomCounts(id, {
        filter: feedView.filter,
        tags: feedView.tags,
        verified: feedView.verified,
        sourced: feedView.sourced,
        by: feedView.by,
        kind: feedView.kind,
      })
      .then(c => {
        if (alive) setFeedFacets(c);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, feedOpen, feedView, liveItemId, facetsTick]);
  // WHILE SHOWING WHAT IS WAITING, the sorter empties the list: read it again
  // on the same slowed tick as the counts, so sorted items drop out.
  useEffect(() => {
    if (state && feedWaiting) loadItems(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveItemId]);
  const feedNarrowed = Boolean(feedView.tags.length || feedView.verified || feedView.sourced || feedView.by);
  const narrowed = feedNarrowed && feedFacets ? feedFacets.filters : null;

  // The report follows the same lens and the same liveness as the facets. The
  // bucket and the lookup state are feed concepts and do not reach it.
  // ON THE BOARD, only for the card that is open, and only when it is the
  // table the address names. This used to fetch on the tables view alone, so
  // an opened card on the board said "No report yet" whatever was in it.
  const boardCardOpen = stage === 'board' && expandedKind === `${report}:${place.subject ?? ''}` && report !== 'who_ranks';
  // A CUT IS ITS TABLE WITH A FILTER OF ITS OWN, fetched here like the table,
  // with the cut's filter added to the lens. Without this the tab was missing
  // and the board drew the whole table under the cut's name.
  const openCut = !isCardKind(report) && place.subject ? findCard(report, place.subject) : null;
  const cutKey = openCut ? `${openCut.id}:${JSON.stringify(openCut.lens ?? null)}` : '';
  useEffect(() => {
    if (!state || !boardCardOpen) return;
    // A chat-built card fetches for itself, by subject.
    if (isCardKind(report)) return;
    let alive = true;
    setReportLoading(true);
    const lens = cutLens(view, openCut?.lens);
    api
      .getBoardroomReport<BoardroomReport>(id, report, { tags: lens.tags, verified: lens.verified, sourced: lens.sourced, by: lens.by, b: crossBy })
      .then(r => {
        if (alive) setReportData(r);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setReportLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, stage, boardCardOpen, report, cutKey, view.tags, view.verified, view.sourced, view.by, crossBy, liveItemId, facetsTick]);

  useEffect(() => {
    if (!state || stage !== 'profile' || profileId === null) return;
    let alive = true;
    api
      .getBoardroomProfile(id, profileId)
      .then(p => {
        if (alive) setProfile(p);
      })
      .catch(() => {
        if (alive) setProfile(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, stage, profileId, liveItemId, facetsTick]);

  useEffect(() => {
    api
      .listBoardroomRuns(id, 1)
      .then(rs => setLastRun(rs.find(r => r.status !== 'RUNNING') ?? null))
      .catch(() => undefined);
  }, [id]);

  const startRun = async (
    intent: BoardroomRunIntent,
    tokenBudget: number,
    directive: string | null = null,
    focus: { archetype: string; item_id: number | null } | null = null,
    scope: StagedDig['scope'] = null,
  ) => {
    setError(null);
    try {
      const run = await api.startBoardroomRun(id, {
        intent,
        token_budget: tokenBudget,
        directive,
        focus_archetype: focus?.archetype ?? null,
        focus_item_id: focus?.item_id ?? null,
        scope,
      });
      setStaged(null);
      setPaywall(null);
      setState(s => (s ? { ...s, room: { ...s.room, run } } : s));
    } catch (e) {
      const pw = paywallOf(e);
      if (pw) {
        setPaywall(pw);
        return;
      }
      const msg = e instanceof Error ? e.message : 'Could not start the run';
      // The server knows better than a stale page: adopt its run instead of
      // leaving the buttons up and the error on screen.
      if (/already has a run/i.test(msg)) {
        try {
          const room = await api.getBoardroom(id);
          setState(s => (s ? { ...s, room: { ...s.room, run: room.run, personas: room.personas } } : s));
          return;
        } catch {
          /* fall through to the message */
        }
      }
      setError(msg);
    }
  };

  /** `directive` overrides the staged one when given: the box hands over what
   *  is typed at the moment of the click, which can be ahead of the staged state. */
  const startStaged = (tokenBudget: number, directive?: string) => {
    if (!staged && !directive?.trim()) return Promise.resolve();
    const s = staged ?? { directive: '', archetype: null, item_id: null, scope: null, intent: 'DEEPER' as const, source: 'typed here' };
    return startDig(s, tokenBudget, directive);
  };
  const startDig = (s: StagedDig, tokenBudget: number, directive?: string) => {
    // AN ITEM IS A FOCUS ON ITS OWN. This required an archetype, so a dig aimed
    // at a row the room had already finished — no holes, so no seat named —
    // started unaimed at the whole board. The server drops an empty archetype
    // and keeps the item.
    const focus = digFocus(s);
    const text = (directive ?? s.directive).trim() || null;
    return startRun(s.intent, tokenBudget, text, focus, s.scope);
  };

  /** "Process N results": the server sizes the budget to what is waiting. */
  const processResults = async () => {
    setError(null);
    setStarting(true);
    try {
      const run = await api.processBoardroomResults(id);
      setPaywall(null);
      setState(s => (s ? { ...s, room: { ...s.room, run } } : s));
    } catch (e) {
      const pw = paywallOf(e);
      if (pw) setPaywall(pw);
      else setError(e instanceof Error ? e.message : 'Could not start processing');
    } finally {
      setStarting(false);
    }
  };

  const stopRun = async (run: BoardroomRun) => {
    setError(null);
    try {
      const stopped = await api.stopBoardroomRun(id, run.id);
      setLastRun(stopped);
      setState(s => (s ? { ...s, room: { ...s.room, run: null } } : s));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not stop the run');
    }
  };

  /** ADMIN ONLY: every item in the room deleted, so it can be dug again from an
   *  empty board. The page reloads after, because every table, count and
   *  report on it was read from the rows that are now gone — and the unseen
   *  marks go first, or the reload would read the badges and glows back from
   *  storage for items that no longer exist. */
  const clearItems = async (opts: { frame?: boolean } = {}) => {
    if (!state) return;
    if (
      !window.confirm(
        opts.frame
          ? `Delete EVERY item in "${state.room.name}", and its frame? Findings, hunches, ideas, lookups and the axes all go, so the next dig frames the room from the start. The room and its personas stay. This cannot be undone.`
          : `Delete EVERY item in "${state.room.name}"? Findings, hunches, ideas and lookups all go. The room, its personas and its frame stay. This cannot be undone.`,
      )
    )
      return;
    setError(null);
    try {
      await api.clearBoardroomItems(id, opts);
      setBucketState({ room: id, buckets: {} });
      clearBuckets(id);
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear the room');
    }
  };

  /** The Tags tab's hover ×: one dimension:value gone from every item in the room. */
  const removeTagValue = async (dimension: string, value: string) => {
    const label = `${dimension.toLowerCase()}: ${value}`;
    const n = facets?.tag_facets[dimension]?.find(v => v.value === value)?.count;
    if (!window.confirm(`Remove the tag "${label}" from ${n != null ? `${n} item${n === 1 ? '' : 's'}` : 'every item'} in this room?`)) return;
    setError(null);
    try {
      const res = await api.deleteBoardroomTagValue(id, dimension, value);
      const key = `${res.dimension}:${res.value}`;
      const gone = new Set(res.item_ids);
      setState(s => {
        if (!s) return s;
        const items = s.items.map(i =>
          gone.has(i.id) ? { ...i, tags: i.tags.filter(t => !(t.dimension === res.dimension && t.value === res.value)) } : i,
        );
        const bucket = (s.room.tag_facets[res.dimension] ?? []).filter(v => v.value !== res.value);
        const tag_facets = { ...s.room.tag_facets, [res.dimension]: bucket };
        return { ...s, items, room: { ...s.room, tag_facets } };
      });
      setView(v => (v.tags.includes(key) ? { ...v, tags: v.tags.filter(t => t !== key) } : v));
      setFacetsTick(t => t + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the tag');
    }
  };

  const personaChanged = (p: BoardroomPersona) =>
    setState(s => {
      if (!s) return s;
      const at = s.room.personas.findIndex(x => x.id === p.id);
      const personas = at >= 0 ? s.room.personas.map((x, i) => (i === at ? p : x)) : [...s.room.personas, p];
      return { ...s, room: { ...s.room, personas } };
    });

  const upsert = (item: BoardroomItem) =>
    setState(s => {
      if (!s) return s;
      const items = matchesView(item, view) ? upsertItem(s.items, item) : s.items.filter(i => i.id !== item.id);
      return { ...s, items };
    });

  const add = async (body: NewItem) => {
    const item = await api.createBoardroomItem(id, body);
    // The socket will also deliver it; upserting here makes the form feel
    // immediate and is idempotent by id.
    setState(s => {
      if (!s) return s;
      const isNew = s.room.last_item_id === null || item.id > s.room.last_item_id;
      const room = isNew ? { ...s.room, last_item_id: item.id } : s.room;
      return { room, items: matchesView(item, view) ? upsertItem(s.items, item) : s.items };
    });
  };

  // FOLLOWING A LEAD, THE TABLES LOOK THROUGH IT. An address without the
  // lead's tags (an old link, a crumb, a lens cleared elsewhere) gets them
  // back; the way out of a lead is its bar's Back, not the lens.
  //
  // AND WHEN THE LEAD CHANGES - here, in the chat, or on another open page -
  // the tables go to exactly the new lead's lens (or the whole room). Not on
  // the room's first load: an address with more than the lead keeps it.
  const followed = state?.room.lead ?? null;
  const lensTags = useMemo(() => splitLensTags(view.tags, followed?.lens.tags), [view.tags, followed]);
  const roomLoaded = Boolean(state);
  const lastLead = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (!roomLoaded) return;
    const now = followed?.id ?? null;
    const before = lastLead.current;
    lastLead.current = now;
    if (before !== undefined && before !== now) {
      setView(v => viewForLead(v, followed));
      return;
    }
    if (followed && !insideLead(view, followed)) setView(v => withLead(v, followed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomLoaded, followed?.id, location.search]);
  // "Follow this lead", open under the lens bar.
  const [followOpen, setFollowOpen] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  useEffect(() => setFollowOpen(false), [followed?.id]);

  if (error && !state) {
    return (
      <div className="p-6 text-sm text-red-600 dark:text-red-400" role="alert">
        {error} · <Link to="/boardroom" className="underline">back to recons</Link>
      </div>
    );
  }
  if (!state) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const { room, items } = state;

  // ---- leads ----
  const applyLeads = (block: Partial<BoardroomDetail>) =>
    setState(s => (s ? { ...s, room: { ...s.room, ...block } } : s));
  /** Follow a lead, or (null) the whole room. The tables go to exactly its lens. */
  const goLead = async (leadId: number | null) => {
    setLeadBusy(true);
    setError(null);
    try {
      const block = await api.followBoardroomLead(id, leadId);
      applyLeads(block);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not follow that lead');
    } finally {
      setLeadBusy(false);
    }
  };
  const followLens = async (body: { title: string; text: string }) => {
    setError(null);
    try {
      const block = await api.createBoardroomLead(id, {
        ...body,
        lens: lensOfView(view),
        parent_id: room.lead?.id ?? null,
      });
      applyLeads(block);
      setFollowOpen(false);
      setNote(`Following ${body.title}. Digs stay inside it until you go back.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not follow this lead');
      throw e;
    }
  };
  const editLead = async (body: { title: string; text: string }) => {
    if (!room.lead) return;
    try {
      applyLeads(await api.editBoardroomLead(id, room.lead.id, body));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the lead');
      throw e;
    }
  };
  const leadToDirection = async () => {
    const lead = room.lead;
    if (!lead) return;
    if (
      !window.confirm(
        `Make "${lead.title}" the room's direction? What it is looking for becomes the room's focus, and the tables go back to the whole room.`,
      )
    )
      return;
    setLeadBusy(true);
    try {
      const res = await api.boardroomLeadToDirection(id, lead.id);
      applyLeads(res);
      setNote(`The room's focus is now what "${lead.title}" was looking for.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not make it the room's direction");
    } finally {
      setLeadBusy(false);
    }
  };
  const dropLead = async (lead: BoardroomLead) => {
    if (
      !window.confirm(
        `Drop "${lead.title}"? It and any leads inside it leave the list. Everything found while following it stays on the board.`,
      )
    )
      return;
    setLeadBusy(true);
    try {
      applyLeads(await api.dropBoardroomLead(id, lead.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not drop that lead');
    } finally {
      setLeadBusy(false);
    }
  };
  // WHAT THIS DIG HAS PUT ON THE BOARD, counted off the rows arriving over the
  // socket. Findings only: a dig's lookups are how it works, not what it found.
  // BY KIND, for the status bar: "12 found (5 feature · 4 pricing)". It was a
  // second box on the page being dug into; the bar is where a dig is watched.
  const foundKinds = runFound && runFound.id === room.run?.id ? runFound.added : {};
  const foundThisDig = Object.values(foundKinds).reduce((a, n) => a + n, 0);

  // A ROOM BEING OPENED IS A CONVERSATION AND NOTHING ELSE.
  //
  // Until it knows what it is asking, there is no board to lay out, no answer
  // to show and nothing to steer — and every one of those surfaces on screen at
  // once is how a person ends up typing a subject into a box and letting the
  // room guess the rest. Room 26 was guessed at, and spent 25.8M tokens on the
  // wrong reading of one sentence.
  //
  // Read off the QUESTION, not a flag: a room has been opened when somebody has
  // said what it is for. It crosses over the moment the chat writes one, and it
  // never comes back.
  if (!room.description.trim()) {
    return (
      <div className="flex flex-col min-h-0" style={{ height: 'calc(100vh - 7rem)' }}>
        <div className="mb-2 flex items-center gap-3">
          <Link
            to="/boardroom"
            className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <span aria-hidden>←</span> Recons
          </Link>
          <h1 className="min-w-0 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
            A new room
          </h1>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <StageModelsWidget surface="boardroom" returnLabel="the recon" className="shrink-0" />
            {/* A room started by mistake is deleted from here too: this header
                had no menu, so a new room could not be deleted at all. */}
            <RoomMenu
              items={[
                {
                  key: 'delete',
                  label: 'Delete recon (nothing found yet)',
                  danger: true,
                  onSelect: deleteRoom,
                  title: 'Remove this recon from your list',
                  testId: 'delete-room',
                },
              ]}
            />
          </div>
        </div>
        {/* Scrolls while the form is up, so the chat below it keeps a usable height. */}
        <div className="mx-auto flex w-full max-w-3xl min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {!conversing && !firstSay && <StartForm boardroomId={id} onSay={setFirstSay} onTables={loadBoard} />}
          {/* A shape that worked before. Offered here rather than in a form,
              because this is the first moment it can be ranked against what the
              person actually says. */}
          <RecipePicker
            subject={recipeSubject}
            value={null}
            onChange={async recipeId => {
              if (!recipeId) return;
              try {
                const fresh = await api.applyBoardroomRecipe(id, recipeId);
                setState(st => (st ? { ...st, room: { ...st.room, ...fresh } } : st));
                loadBoard();
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Could not use that recipe');
              }
            }}
          />
          {!conversing && !firstSay && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Or say it in your own words:</p>
          )}
          <div
            className={`min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 ${
              conversing || firstSay ? '' : 'min-h-[22rem] shrink-0'
            }`}
          >
            <BoardroomChatPane
              boardroomId={id}
              room={{ question: '', digging: false, findings: 0, axes: [] }}
              say={firstSay}
              onSayUsed={clearFirstSay}
              welcome={false}
              onConversation={markConversing}
              onSaid={setRecipeSubject}
              onChanged={onChatChanged}
            />
          </div>
          {error && (
            <div className="text-xs text-red-600 dark:text-red-400" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  const frameWaiting = framesAwaiting(room.frame ?? null, room.frame_proposals ?? []);
  // THE BRIEF OUTLIVES THE DIG. `room.run` is only the dig running now, so the
  // director's read vanished the moment one ended; the last one stands in.
  const briefRun = room.run ?? lastRun;
  // A report cell has an id, not a row; the drawer wants the row.
  // Companies, products and types have a page of their own; the rest open in
  // the drawer. The row is fetched either way, so the kind decides.
  // THE ROW, NOT THE TREE: the kind is all this needs, and the drawer loads
  // its own detail. Opening a type from a tag used to fetch every one of its
  // 853 children first (5.7s).
  const openById = (itemId: number) => {
    api
      .getBoardroomItemRow(id, itemId)
      .then(row => {
        if ((PROFILE_KINDS as readonly string[]).includes(row.kind)) openProfile(row.id);
        else setOpen(row);
      })
      .catch(() => undefined);
  };
  const editById = (itemId: number) => {
    api
      .getBoardroomItemRow(id, itemId)
      .then(setOpen)
      .catch(() => undefined);
  };
  // Under the feed's author filter its counts carry the narrowed numbers;
  // otherwise the room's own totals.
  const lookupCounts = (feedFacets?.lookup_counts ?? room.lookup_counts) ?? EMPTY_LOOKUP_COUNTS;
  const rail = (
    <FilterRail
      room={room}
      filter={feedView.filter}
      tags={feedView.tags}
      onFilter={f => setFeedView(v => ({ ...v, filter: f, lookup: f === 'lookups' ? v.lookup : null, kind: f === 'lookups' ? v.kind : null }))}
      onTag={key => setFeedView(v => ({ ...v, tags: toggleTag(v.tags, key) }))}
      verified={feedView.verified}
      onVerified={x => setFeedView(v => ({ ...v, verified: x }))}
      sourced={feedView.sourced}
      onSourced={x => setFeedView(v => ({ ...v, sourced: x }))}
      sourcedCounts={feedFacets?.sourced_counts ?? null}
      onClear={() => {
        setFeedView(EMPTY_VIEW);
        setFeedWaiting(null);
      }}
      alsoActive={Boolean(feedView.by || feedView.lookup || feedView.kind)}
      lookups={
        <LookupActivity
          heading={null}
          counts={lookupCounts}
          kindCounts={(feedNarrowed && feedFacets ? feedFacets.item_counts : room.item_counts) ?? {}}
          kind={feedView.kind}
          state={feedView.lookup}
          onPick={next => setFeedView(v => ({ ...v, filter: 'lookups', ...next }))}
        />
      }
      narrowed={narrowed}
      verifiedCounts={feedFacets?.verified_counts ?? null}
      sorting={
        <>
          <TaggingActivity
            tagging={facets?.tagging ?? room.tagging}
            waiting={feedWaiting}
            onWaiting={setFeedWaiting}
            onSetBudget={async tokens => {
              try {
                const tagging = await api.setBoardroomSortingBudget(id, tokens);
                setFacets(f => (f ? { ...f, tagging } : f));
                setState(s => (s ? { ...s, room: { ...s.room, tagging } } : s));
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Could not change the sorting budget');
              }
            }}
          />
          <AppealActivity appeal={facets?.appeal} waiting={feedWaiting} onWaiting={setFeedWaiting} />
        </>
      }
    />
  );
  // WHAT THE ROOM IS DOING, under the seats rather than in the rail: the rail
  // is only mounted on the feed, so from Reports a room with eight fetches in
  // flight looked idle. It sits below "In the room" because it is the same
  // question — who is working — for the work nobody is sitting on. Status only:
  // clicking here changed a filter you could not see, so the filtering lives
  // under Lookups in Feed & filters instead.

  // ---- The side panels' contents, the same whether slid out or pinned ----
  const feedBody = (
    <div className="min-h-0 flex-1 flex flex-col gap-2">
      {/* The feed scrolls itself; this is the height it has to do it in. */}
      <div className="min-h-0 flex-1">
      <ItemFeed
            items={items}
            personas={personas}
            hasMore={hasMore}
            loading={loadingItems}
            onLoadMore={() => loadItems(items.length ? items[0].id : null)}
            onOpen={setOpen}
            emptyText={
              feedFiltered ? (
                /* NAME THE LENS. "Nothing here" under three stacked filters read as
                   a broken tag filter when it was a Pains bucket left selected —
                   the tag matched 9 items, none of them pains. */
                <span>
                  Nothing matches{' '}
                  {[
                    feedWaiting
                      ? `${WAITING_LABELS[feedWaiting.queue]}${feedWaiting.kind ? ` (${KIND_LABELS[feedWaiting.kind]})` : ''}`
                      : null,
                    feedView.filter ? room.filters.find(f => f.key === feedView.filter)?.label ?? feedView.filter : null,
                    feedView.tags.length ? `tags ${tagsLabel(feedView.tags)}` : null,
                    feedView.verified ? `validation: ${VERIFIED_OPTIONS.find(o => o.key === feedView.verified)?.label ?? feedView.verified.toLowerCase()}` : null,
                    feedView.sourced ? (feedView.sourced === 'UNSOURCED' ? 'unsourced' : 'sourced') : null,
                    feedView.by ? `by ${feedView.by === 'human' ? 'you' : personas.get(Number(feedView.by))?.name ?? `persona #${feedView.by}`}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  .
                  {feedView.filter && (feedView.tags.length || feedView.verified || feedView.sourced || feedView.by) && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => setFeedView(v => ({ ...v, filter: null }))}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Show every kind
                      </button>
                    </>
                  )}
                </span>
              ) : (
                'Nothing on the board yet. Add a note or queue a lookup below.'
              )
            }
          />
      </div>
      <AddItemForm onAdd={add} />
    </div>
  );
  // WHAT IS OPEN, for the chat: "this" in a message means it.
  const expandedCard = cardTitle(expandedKind);
  // WHERE BACK GOES, by name. A board step with a card open is that card: it
  // said "Back to Board" and reopened the Feature cloud.
  const back = backStep(trail, (location.state as { from?: string } | null)?.from);
  const backCrumb = back?.crumb ?? null;
  const backLabel = backCrumb
    ? (backCrumb.key.startsWith('board') && cardTitle(placeFromSearch(backCrumb.search, 'board').expanded)) || backCrumb.label
    : null;
  // THE BROWSER'S BACK WHEN IT LANDS IN THE SAME PLACE. Opening the saved
  // address instead pushed a new step, so the browser's Back then went
  // forward to the page you had just left. After a reload or a pasted link the
  // browser has nothing of ours behind it, and the saved address is the way.
  const goBack = () => {
    if (!back) return;
    if (back.history) navigate(-1);
    else goSaved(back.crumb.search);
  };
  const chatView = viewPhrase({
    stage,
    table: null,
    profile:
      stage === 'profile' && profile && profile.item.id === profileId
        ? { id: profile.item.id, kind: profile.item.kind, title: profile.item.title }
        : null,
    boardCard: expandedCard,
    tags: view.tags,
    verified: view.verified,
    sourced: view.sourced,
    by: view.by ? (view.by === 'human' ? 'you' : personas.get(Number(view.by))?.name ?? null) : null,
    drawer: open ? { id: open.id, kind: open.kind, title: open.title } : null,
  });
  const chatPane = (controls?: ReactNode) => (
    <BoardroomChatPane
      boardroomId={id}
      room={{
        question: room.description,
        successCriteria: room.success_criteria ?? null,
        digging: room.run?.status === 'RUNNING',
        // Findings, not everything: a board of questions the chat itself
        // wrote is still a room that has not started.
        findings: (room.filters ?? [])
          .filter(f => f.key !== 'understand' && f.key !== 'questions' && f.key !== 'lookups')
          .reduce((n, f) => n + (f.count ?? 0), 0),
        axes: (room.frame?.axes ?? []).map(a => a.dimension),
      }}
      draft={chatDraft}
      onDraftUsed={() => setChatDraft(null)}
      view={chatView}
      onChanged={onChatChanged}
      onOpenItem={openById}
      controls={controls}
    />
  );
  // Cards on a tinted ground, so each reads as its own answer.
  const tinted = (body: ReactNode) => (
    <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900/40 p-4">{body}</div>
  );
  const panelBody = (key: PanelKey, docked = false): ReactNode => {
    if (key === 'chat') {
      return chatPane(
        docked ? (
          <>
            <PinButton pinned onClick={panels.unpin} />
            <button type="button" onClick={() => panels.close('chat')} title="Close" className="icon-btn text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              ×
            </button>
          </>
        ) : undefined,
      );
    }
    if (key === 'feed') {
      // THE FEED IS THE POINT, SO IT IS ALWAYS IN VIEW. Filters on the left,
      // the feed beside them, each scrolling on its own. Stacked, the feed sat
      // under the whole filter list — below the bottom of the screen. In a
      // narrow pinned slot the filters fold up above the feed instead.
      const cramped = docked && chat.width < 560;
      return (
        <div className="min-h-0 flex-1 flex overflow-hidden" data-testid="feed-panel">
          {!cramped && (
            <aside className="w-64 shrink-0 min-h-0 overflow-y-auto border-r border-gray-200 dark:border-gray-700 p-2">{rail}</aside>
          )}
          <div className="min-h-0 flex-1 flex flex-col gap-2 p-2">
            {cramped && (
              <details className="shrink-0 max-h-[45%] overflow-y-auto rounded border border-gray-200 dark:border-gray-700 px-2 py-1">
                <summary className="cursor-pointer text-xs text-gray-600 dark:text-gray-300">
                  Filters{feedFiltered ? ' · on' : ''}
                </summary>
                <div className="pt-1">{rail}</div>
              </details>
            )}
            {feedBody}
          </div>
        </div>
      );
    }
    if (key === 'leads') {
      return (
        <LeadsPanel
          leads={room.leads ?? []}
          current={room.lead?.id ?? null}
          busy={leadBusy}
          onFollow={leadId => void goLead(leadId)}
          onDrop={lead => void dropLead(lead)}
        />
      );
    }
    if (key === 'notes') {
      return <NotesPanel boardroomId={id} onOpenItem={openById} />;
    }
    if (key === 'shared') {
      return (
        <SharedPanel
          boardroomId={id}
          titleOf={deliverableId => board?.deliverables.find(d => d.id === deliverableId)?.title ?? null}
        />
      );
    }
    if (key === 'tags') {
      return (
        <TagsPanel
          load={loadTags}
          refreshKey={`${liveItemId}:${facetsTick}`}
          selected={view.tags}
          onToggle={k => setView(v => ({ ...v, tags: toggleTag(v.tags, k) }))}
          onClear={() => setView(v => ({ ...v, tags: [] }))}
          onRemove={removeTagValue}
          onAddAxis={axis => api.writeBoardroomAxis(id, axis)}
          onRecalculate={() => api.sortBoardroomNow(id)}
          onSaveTaxonomy={text => api.writeBoardroomTaxonomy(id, text)}
          loadSkipped={dimension => api.getBoardroomAxisSkipped(id, dimension)}
          onOpen={openById}
        />
      );
    }
    if (key === 'runs') {
      return (
        <RunsPanel
          boardroomId={id}
          // WHO DID THE WORK, beside the digs: each seat with what it wrote.
          // Clicking one narrows the room's lens to it ("by"). Feed & filters
          // keeps its own filters, so this no longer narrows the feed.
          seats={
            <PersonaStrip
              boardroomId={id}
              personas={room.personas}
              onChanged={personaChanged}
              byCounts={facets?.by_counts ?? null}
              activeBy={view.by}
              onBy={b => setView(v => ({ ...v, by: v.by === b ? null : b }))}
            />
          }
          // A dig starting or ending changes the table.
          refreshKey={`${room.run?.id ?? ''}:${room.run?.status ?? ''}`}
          focusRun={focusRun}
          onOpen={openById}
          onLens={tags => setView(v => ({ ...v, tags }))}
          onTable={k => {
            const card = board?.deliverables.find(d => d.kind === k && !d.subject && d.status !== 'dropped');
            if (card) openOnBoard(card);
            else setReport(k);
          }}
        />
      );
    }
    // "THE ROOM": the answer, then the brief, then how it got here, folded.
    // The answer and the brief were two tabs, so whether the room had an
    // answer and where it was aimed were read in two places.
    return tinted(
      <div className="flex flex-col gap-4" data-testid="room-panel">
        <AnswerBrief
              part="now"
              answer={room.answer ?? null}
              history={room.answer_history ?? []}
              answering={room.answering}
              turnsDone={room.run?.turns_done ?? 0}
              onUse={answerId => onFrame(() => api.useBoardroomAnswer(id, answerId))}
              onDismiss={(answerId, reason) => onFrame(() => api.dismissBoardroomAnswer(id, answerId, reason))}
              // WHAT WOULD CHANGE IT IS A DIG WAITING TO BE ASKED FOR. Staged,
              // not started: it lands in the Next dig strip under the lines,
              // where every other staged dig is read before it spends.
              onDig={directive => {
                setStaged({
                  directive,
                  archetype: null,
                  item_id: null,
                  scope: null,
                  intent: 'DEEPER',
                  source: 'what would change the answer',
                });
                setAnswerOpen(false);
              }}
            />
        <DirectorNote
            hideWorklists
            run={briefRun}
            assignments={room.assignments ?? []}
            plan={room.plan}
            rounds={6}
            personas={personas}
            frameWaiting={frameWaiting}
            // NO "STEER THE ROOM" ANY MORE. Focus and the next dig's directive
            // were a second way to say what the chat says in words — and a
            // second way is a fork: two places to aim a room, neither of which
            // knows what the other was told. What the room is pointed AT still
            // reads here; changing it is a sentence in the chat.
            steer={
              (room.focus?.text || (room.reader_subjects ?? []).length > 0) && (
                <div className="flex flex-col gap-2 text-xs">
                  {room.focus?.text && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Focused on </span>
                      <span className="text-gray-900 dark:text-gray-100">{room.focus.text}</span>
                    </div>
                  )}
                  {(room.reader_subjects ?? []).length > 0 && (
                    <ReaderSubjects subjects={room.reader_subjects ?? []} />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setDirectorOpen(false);
                      setChatOpen(true);
                    }}
                    className="self-start rounded-md border border-indigo-200 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-200 dark:hover:bg-indigo-900/30"
                  >
                    Aim it in the chat
                  </button>
                </div>
              )
            }
            frame={
              // A story room is never framed: its tables sort on nothing.
              !isStoryRoom(room.room_kind) && (
                <FrameLine
                  frame={room.frame ?? null}
                  proposals={room.frame_proposals ?? []}
                  history={room.frame_history ?? []}
                  onWrite={body => onFrame(() => api.writeBoardroomFrame(id, body))}
                  onUse={frameId => onFrame(() => api.useBoardroomFrame(id, frameId))}
                  onAccept={frameId => onFrame(() => api.acceptBoardroomFrame(id, frameId))}
                  onDismiss={frameId => onFrame(() => api.dismissBoardroomFrame(id, frameId))}
                />
              )
            }
          />
        <details className="group rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800" data-testid="room-history">
          <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100">
            How it got here
            <span className="ml-2 text-xs font-normal text-gray-400">earlier answers and the seats' worklists</span>
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            <AnswerBrief
              part="history"
              answer={room.answer ?? null}
              history={room.answer_history ?? []}
              answering={room.answering}
              turnsDone={room.run?.turns_done ?? 0}
              onUse={answerId => onFrame(() => api.useBoardroomAnswer(id, answerId))}
              onDismiss={(answerId, reason) => onFrame(() => api.dismissBoardroomAnswer(id, answerId, reason))}
              // WHAT WOULD CHANGE IT IS A DIG WAITING TO BE ASKED FOR. Staged,
              // not started: it lands in the Next dig strip under the lines,
              // where every other staged dig is read before it spends.
              onDig={directive => {
                setStaged({
                  directive,
                  archetype: null,
                  item_id: null,
                  scope: null,
                  intent: 'DEEPER',
                  source: 'what would change the answer',
                });
                setAnswerOpen(false);
              }}
            />
            <Worklists assignments={room.assignments ?? []} personas={personas} />
          </div>
        </details>
      </div>
    );
  };
  const dot = (tone: string, title: string, testId?: string) => (
    <span title={title} className={`h-1.5 w-1.5 rounded-full ${tone}`} data-testid={testId} />
  );
  // Top to bottom: what you use most first.
  // WHAT FIND CAN JUMP TO without asking the server: every table in the room
  // (the room's own names for them) and every seat.
  // OPEN A CARD IN THE BOARD: the table fills it, the other cards sit beside
  // it as tiles, and "‹ Board" goes back. Clicking a card and picking it in
  // Find both come here, so a table looks the same however you reached it.
  // It is a step you can go Back from, wherever you opened it.
  const openOnBoard = (d: BoardroomDeliverable | null, replace = false) => {
    const expanded = d ? `${d.kind}:${d.subject}` : null;
    const at = { ...place, stage: 'board' as const };
    // The page fetches whatever report is current, so pointing it at this one
    // is what loads the table. EXCEPT the cards that fetch for themselves:
    // `list` and `report` need a subject the report key cannot carry, and
    // `who_ranks` is not a graph builder, so pointing the page at either
    // would spend a request on a table it cannot draw.
    if (d && !isCardKind(d.kind) && d.kind !== 'who_ranks') {
      go({ ...at, report: d.kind as BoardroomReportKey, subject: isCut(d) ? d.subject : null, expanded }, replace);
    } else {
      go({ ...at, expanded }, replace);
    }
  };
  /** Open a table or card on the Dashboard by kind and subject: the room's
   *  saved view when it has one, otherwise the table unsaved, with a button to
   *  keep it (`temporaryView`). */
  const openView = (kind: string, subject: string, replace = false) => {
    const d = (board?.deliverables ?? []).find(x => x.kind === kind && x.subject === subject && x.status !== 'dropped');
    if (d) return openOnBoard(d, replace);
    const at = { ...place, stage: 'board' as const, expanded: `${kind}:${subject}` };
    if (!isCardKind(kind) && kind !== 'who_ranks') go({ ...at, report: kind as BoardroomReportKey, subject: subject || null }, replace);
    else go(at, replace);
  };
  // "SEE EVERYTHING IT FOUND" on an item's or a table's last dig: Recon runs,
  // on the right-hand rail, opened at that dig with its findings showing.
  const showRun = (runId: number) => {
    setFocusRun({ runId, at: Date.now() });
    panels.show('runs');
  };
  const findTargets: FindTarget[] = [
    ...(board?.deliverables.length
      ? board.deliverables
          .filter(d => d.status !== 'dropped')
          .flatMap((d): FindTarget[] => {
            if (isCardKind(d.kind) || isCut(d)) {
              return d.subject
                ? [{ key: `table:${d.id}`, label: d.title, what: 'table', onPick: () => openOnBoard(d) }]
                : [];
            }
            return REPORT_TABS.some(t => t.key === d.kind)
              ? [{ key: `table:${d.id}`, label: d.title, what: 'table', onPick: () => openOnBoard(d) }]
              : [];
          })
      : REPORT_TABS.map(t => ({
          key: `table:${t.key}`,
          label: String(t.label),
          what: 'table',
          onPick: () => setReport(t.key),
        }))),
    // NO SEATS. They are how the room works, not what it found, and a reader
    // looking for a table or a finding had to skip past them.
  ];
  // WHAT IS OPEN INSIDE THE ROOM, outermost first, for the location bar. An
  // item sits under the table it was opened from, when there was one.
  const locationPath: PathCrumb[] = (() => {
    // THE BOARD IS HOME: a table opened in full sits under it, and so does a
    // card opened on the board, and an item opened from it.
    const toBoard = { key: 'board', label: 'Dashboard', onClick: () => go({ ...place, stage: 'board', expanded: null }) };
    if (stage === 'profile') {
      const from = tableBefore(trail);
      const known = profile && profile.item.id === profileId;
      const here = { key: crumbKey(place), label: placeLabel(place, known ? profile!.item.title : null) };
      if (from?.card) {
        // OPENED FROM A CARD ON THE BOARD: Board / the card / the item. The
        // card's crumb reopens it; Board is the board with nothing open.
        const title = cardTitle(from.card);
        return [toBoard, { key: `card:${from.card}`, label: title || 'Card', onClick: () => goSaved(from.search) }, here];
      }
      return [from ? { key: from.key, label: from.label, onClick: () => goSaved(from.search) } : toBoard, here];
    }
    if (stage === 'board') {
      return expandedCard
        ? [toBoard, { key: `card:${expandedKind}`, label: expandedCard }]
        : [{ key: 'board', label: 'Dashboard' }];
    }
    return [toBoard, { key: crumbKey(place), label: visitName().label }];
  })();
  // EVERY VIEW THIS ROOM CAN SHOW: the All views panel over the Dashboard.
  /** Put a view on the dashboard by its table: the room's card for it back on
   *  the list (or back from removed), or a new one. */
  const addView = async (kind: string) => {
    const have = (board?.deliverables ?? []).find(d => d.kind === kind && !d.subject);
    if (have && have.status === 'dropped') return patchDeliverable(have.id, { status: 'open', pinned: true });
    if (have) return pinCard(have, true);
    const seq = ++patchSeq.current;
    try {
      const fresh = await api.addBoardroomDeliverable(id, { kind, pinned: true });
      if (seq === patchSeq.current) setBoard(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that view');
    }
  };
  const browserFor = (onClose: () => void, embedded = false) => (
    <DeliverableBrowser
      deliverables={board?.deliverables ?? []}
      catalogue={board?.catalogue ?? []}
      onPin={d => {
        // NO NAVIGATION. "On the board" now moves the tab strip too, so
        // the change is visible wherever you pressed it — and being
        // yanked to the board mid-browse was the worse half of the bug
        // this replaced.
        pinCard(d, !d.pinned);
      }}
      onAdd={async (kind, pinned) => {
        const seq = ++patchSeq.current;
        try {
          // EVERY TABLE THE ROOM HAS IS ONE ITS DIGS WORK ON, pinned or not.
          const fresh = await api.addBoardroomDeliverable(id, { kind, pinned });
          if (seq === patchSeq.current) setBoard(fresh);
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Could not add that table');
        }
      }}
      onDelete={d => void deleteCard(d)}
      // BACK ON THE DASHBOARD, not just back in the room.
      onRestore={d => void patchDeliverable(d.id, { status: 'open', pinned: true })}
      onClose={onClose}
      embedded={embedded}
    />
  );

  // THREE TABS (see RightRail's GROUPS). The badges the old tabs carried
  // move with their panels: a decision waiting on the chat, a frame change
  // or a followed lead on Room settings, a running dig on Activity.
  const railTabs: RailTab[] = [
    {
      key: 'chat',
      // DRAWN, NOT AN EMOJI: in the rail's own text colour and weight, so it
      // reads as part of the tab and takes the highlight with it. The emoji
      // was a white cartoon on a dark rail.
      icon: (
        <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
          <path d="M2.5 3.5h11v7h-6.5l-3 2.5v-2.5h-1.5z" />
        </svg>
      ),
      label: 'Chat',
      title: 'Tell the room what to chase, ask what it has found, or stop it',
      badge: decisionWaiting ? dot('bg-amber-500', 'The room needs you to settle something', 'decision-waiting') : undefined,
    },
    {
      key: 'tags',
      label: view.tags.length ? `Tags · ${view.tags.length}` : 'Tags',
      title: "The room's tags: pick one to narrow the whole room, the dashboard and every table",
      badge: view.tags.length ? dot('bg-violet-500', 'The room is narrowed by tag') : undefined,
    },
    {
      key: 'activity',
      label: `Activity${feedFiltered ? ' · filtered' : ''}`,
      title: 'What the room is doing: every item as it lands, each dig it ran, and where you have been',
      badge: room.run?.status === 'RUNNING' ? dot('bg-emerald-500', 'A dig is running') : undefined,
    },
    {
      key: 'settings',
      label: 'Room settings',
      title: "The room's question and setup, the leads you follow, and what you shared",
      badge:
        frameWaiting > 0
          ? dot('bg-amber-500', 'A setup change is waiting for you')
          : room.lead
            ? dot('bg-indigo-500', `Following ${room.lead.title}`)
            : undefined,
    },
  ];
  // The strip's names, with the counts the old tabs showed.
  const panelLabels: Partial<Record<PanelKey, ReactNode>> = {
    ...PANEL_LABEL,
    feed: `Feed${feedFiltered ? ' · filtered' : ''}`,
    leads: `Leads${room.leads?.length ? ` · ${room.leads.length}` : ''}`,
    brief: (
      <>
        {PANEL_LABEL.brief}
        {frameWaiting > 0 && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />}
      </>
    ),
  };

  return (
    <FreshProvider value={freshIds}>
    {/* ONE TEXT FILTER FOR THE ROOM, from the top bar: every table under it
        narrows, and so do the board's cards. */}
    <TableFilterContext.Provider value={tableFilter}>
    {/* EVERY TAG CHIP shows whether it is the lead's or the lens's. */}
    <LensTagsContext.Provider value={lensTags}>
    {/* THE PAGE SCROLLS, NOT JUST THE BOARD. Locked to the screen, the title,
        answer and status rows ate the top of every view and the board got
        what was left. Now they scroll off the top, and the work area below
        has a full screen of its own to fill. */}
    <div className="flex flex-col">
      {/* THE TITLE ROW IS THE ROOM AND WHAT YOU CAN DO TO IT. The question used
          to sit here as grey small print, a line away from its own answer; it
          lives above the answer now. Room-level actions sit at the end: Share
          (it shares the room, not the answer it used to sit beside), and the
          rare destructive ones behind ⋯. */}
      {/* ONE HEADER: the room, its answer, and what it is doing, in one card.
          They were three boxes stacked above a breadcrumb row, and together
          took a third of the screen before any finding. The question is said
          once, in the answer row, right above its answer.
          NOT overflow-hidden: it cut the ⋯ menu off at the card's edge, and in
          a short header "Delete recon" was below it. */}
      <div
        className="mb-2 rounded-lg border border-gray-200 bg-white divide-y divide-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:divide-gray-700"
        data-testid="room-header"
      >
      <div className="flex items-center gap-3 min-w-0 px-3 py-2">
        <div className="min-w-0 flex-1">
          <h1
            className="min-w-0 truncate text-lg font-semibold text-gray-900 dark:text-gray-100"
            title={room.success_criteria ? `${room.name} · done when: ${room.success_criteria}` : room.name}
          >
            {room.name}
          </h1>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/* Which models this room bills, with a link to the pipeline mapping.
              Renders nothing unless the admin has revealed internals — the
              widget checks that itself, as it does on the brainstorm page. */}
          <StageModelsWidget surface="boardroom" returnLabel="the recon" className="shrink-0" />
          {/* ADMIN, INTERNALS ONLY: wipe the board to rerun a test. The same
              action as "Clear all items" in the room menu, one click closer —
              shown under the same rule as the models widget beside it. */}
          {showInternals && (
            <button
              type="button"
              onClick={() => clearItems({ frame: true })}
              disabled={room.run?.status === 'RUNNING'}
              title={
                room.run?.status === 'RUNNING'
                  ? 'Stop the dig before clearing the board'
                  : 'Admin: delete every item and the frame, to watch the room from the beginning'
              }
              className="shrink-0 px-2 py-0.5 rounded-md border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="clear-room-items-button"
            >
              Clear items
            </button>
          )}
          <ShareArtifactControl
            kind="BOARDROOM_REPORT"
            boardroomId={id}
            className="shrink-0"
            // THE WHOLE SHARE HISTORY is one click from Share: links to single
            // views live in Room settings → Shared, which nobody found.
            footer={close => (
              <button
                type="button"
                onClick={() => {
                  close();
                  panels.show('shared');
                }}
                className="text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                data-testid="share-all-links"
              >
                Every link shared from this room, single views too →
              </button>
            )}
          />
          <RoomMenu
            items={[
              {
                // THE SAME PANEL AS THE RAIL'S Room settings tab: the ⋯ menu is
                // where people looked for it first.
                key: 'settings',
                label: 'Room settings',
                onSelect: () => panels.toggleGroup('settings'),
                title: "The room's question and setup, the leads you follow, and what you shared",
                testId: 'room-settings',
              },
              // A story room has no market to compare.
              ...(isStoryRoom(room.room_kind)
                ? []
                : [
                    {
                      key: 'compare',
                      label: 'Compare companies',
                      onSelect: () => openCompare(),
                      title: 'Two to four companies side by side: price, pitch, features and complaints',
                      testId: 'room-compare',
                    },
                  ]),
              {
                key: 'save-recipe',
                // KEEPING THE SHAPE, NOT THE FINDINGS. What travels is the
                // tables, the seats and the first questions — see
                // `jcseo/boardroom/recipes.py`.
                label: 'Save this setup as a recipe',
                onSelect: saveAsRecipe,
                title:
                  'Keep how this room is set up - its tables, seats and first questions - to start the next room from',
                testId: 'save-as-recipe',
              },
              {
                key: 'delete',
                // Nothing to lose yet: said so, so a room started by mistake
                // is plainly safe to drop.
                label: Object.values(room.item_counts ?? {}).some(n => n > 0)
                  ? 'Delete recon'
                  : 'Delete recon (nothing found yet)',
                danger: true,
                onSelect: deleteRoom,
                disabled: room.run?.status === 'RUNNING',
                title:
                  room.run?.status === 'RUNNING'
                    ? 'Stop the dig before deleting this recon'
                    : 'Remove this recon from your list',
                testId: 'delete-room',
              },
              // ADMIN-ONLY, AND SAYS SO: shown with internals on, under the
              // same amber pill as every other admin-only control.
              ...(isAdmin && showInternals
                ? [
                    {
                      key: 'clear',
                      label: 'Clear all items',
                      admin: true,
                      danger: true,
                      onSelect: () => clearItems(),
                      disabled: room.run?.status === 'RUNNING',
                      title:
                        room.run?.status === 'RUNNING'
                          ? 'Stop the dig before clearing the board'
                          : 'Admin: delete every item in this room',
                      testId: 'clear-room-items',
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>
      {/* ONE CARD, TWO ROWS: the answer, and what the room is doing. They were
          two boxes that looked alike and opened differently, each with its door
          a screen's width from its words. Each row is now its own door — into
          the answer's slideout, and into the room brief — and the tables below
          keep their height either way.
          THE ANSWER, AT THE FRONT OF THE ROOM. Everything below it — the feed,
          the eight tables — is what it rests on, so it goes above the `stage`
          branch and both views get it. */}
        <AnswerLine
          answer={room.answer ?? null}
          history={room.answer_history ?? []}
          answering={room.answering}
          turnsDone={room.run?.turns_done ?? 0}
          question={room.description}
          onOpen={() => setAnswerOpen(true)}
          lead={room.lead?.title ?? null}
        />
      {/* THE ONE THING TO PRESS, and proof that pressing it did something.
          A dig takes until the next tick to reach its first seat, so the
          seconds after the button have to say "waking the room" rather than
          nothing at all — watched live, that silence read as a dead button. */}
      <RoomStatusBar
        bare
        // WHAT THE DIG IS FOR, in the Research window's words, beside Stop.
        runLabel={
          room.run?.status === 'RUNNING'
            ? runLabel(
                room.run,
                key => REPORT_TABS.find(t => t.key === key)?.label?.toString(),
                // ITS NAME, not "#38417", when the page knows it.
                itemId =>
                  (profile && profile.item.id === itemId ? profile.item.title : undefined) ??
                  items.find(i => i.id === itemId)?.title,
                leadId => (room.leads ?? []).find(l => l.id === leadId)?.title ?? (room.lead?.id === leadId ? room.lead.title : undefined),
              )
            : null
        }
        // Moved up with the run box gone: a refused start and a missing model
        // must still be said, or the button looks like it did nothing.
        notice={
          paywall ? (
            <PaywallNotice paywall={paywall} compact />
          ) : lastRun && lastRun.status !== 'RUNNING' && lastRun.ended_reason === 'NO_MODEL' ? (
            <div className="text-xs text-amber-700 dark:text-amber-300" data-testid="no-model">
              {isAdmin ? (
                <>
                  The model policy left Recon cast with no model that can run, so no seat could take a
                  turn.{' '}
                  <Link to={agentSettingsUrl('boardroom_persona', settingsReturn, { open: true })} className="underline">
                    Choose one in the model policy →
                  </Link>
                </>
              ) : (
                'The room could not start: no AI model is available for it right now. Please contact support.'
              )}
            </div>
          ) : null
        }
        room={{
          question: room.description,
          running: room.run?.status === 'RUNNING',
          runsDone: room.runs_done ?? 0,
          seats: room.personas.filter(p => p.is_active).length,
          staged: stagedHasContent(staged)
            ? { what: staged?.directive?.trim() || 'the dig you lined up' }
            : null,
          // THE TABLE BEING READ. "Dig deeper" while looking at the price map
          // means the price map — it used to mean the room, so the one screen
          // being watched was the one place nothing landed.
          table: tableBeingRead,
          toProcess: room.to_process ?? null,
          nextDig: room.next_dig ?? null,
        }}
        activity={{
          working: room.personas
            .filter(p => p.state === 'working')
            .map(p => p.name),
          fetching: (room.lookup_counts ?? EMPTY_LOOKUP_COUNTS).fetching,
          landed: (room.lookup_counts ?? EMPTY_LOOKUP_COUNTS).landed,
          turns: room.run?.turns_done ?? 0,
          found: foundThisDig,
          startedAt: room.run?.status === 'RUNNING' && room.run.created_at ? feedTime(room.run.created_at) : null,
          foundKinds: foundThisDig ? addedWords(foundKinds, 6) : null,
        }}
        tokens={
          room.run
            ? {
                billed: room.run.tokens_billed ?? room.run.tokens_spent,
                sent: room.run.tokens_spent,
                budget: room.run.token_budget,
              }
            : null
        }
        starting={starting}
        preparing={preparing}
        onShowFound={runFound ? () => showRun(runFound.id) : undefined}
        onStart={async (intent, table) => {
          // AIMED AT A TABLE: the same as the table's own Dig button - the run
          // carries the table, so every round works it rather than drifting
          // back to the room.
          if (table) {
            return draftDig(
              { kind: 'table', report: table as BoardroomReportKey },
              `the ${REPORT_TABS.find(t => t.key === table)?.label?.toString().toLowerCase() ?? 'table'} table`,
              () => templateDig('', 'DEEPER', null, 'a Dig button'),
            );
          }
          setStarting(true);
          try {
            // WIDER drops whatever was lined up and opens new ground; anything
            // else runs what is staged, which is what the button just said.
            if (intent === 'WIDER' || !stagedHasContent(staged)) {
              setStaged(null);
              await startRun(intent, budget);
            } else {
              await startStaged(budget);
            }
          } finally {
            setStarting(false);
          }
        }}
        onResearch={() => {
          // "Ask for something else" drops what was lined up: the window is
          // about to write a new one, and two competing digs on screen is how
          // the staged strip got ignored in the first place.
          setStaged(null);
          research();
        }}
        onProcess={processResults}
        onStop={() => room.run && stopRun(room.run)}
        onStaff={() => setPeopleOpen(true)}
      />
      </div>
      {/* A PROPOSED CHANGE TO THE AXES, where you are. It was a card inside
          Room brief with a dot on the button, and went unseen unless you opened
          the brief — while it decided how every item in the room is sorted. */}
      {frameWaiting > 0 && (
      <div className="mb-2">
        <FrameProposalBanner
          frame={room.frame ?? null}
          proposals={room.frame_proposals ?? []}
          onAccept={frameId => onFrame(() => api.acceptBoardroomFrame(id, frameId))}
          onDismiss={frameId => onFrame(() => api.dismissBoardroomFrame(id, frameId))}
          onReview={() => setDirectorOpen(true)}
        />
      </div>
      )}

      {/* WHAT A NOTICING WOULD ADD, looked at before deciding. */}
      {previewNotice && (
        <Drawer
          onClose={() => setPreviewNotice(null)}
          width="w-[48rem] max-w-full"
          eyebrow="Noticed"
          title={previewNotice.title}
          fill
        >
          <div className="flex h-full min-h-0 flex-col gap-2">
            {/* Its own padding: a `fill` drawer gives the body none. */}
            <div className="space-y-2 px-4 pb-1 pt-3">
            <p className="text-xs text-gray-600 dark:text-gray-300">{previewNotice.why}</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={noticeBusy}
                onClick={() => answerNotice(previewNotice, 'take')}
                className="rounded border border-violet-400 bg-violet-50 px-2 py-0.5 text-xs text-violet-900 hover:bg-violet-100 disabled:opacity-50 dark:border-violet-600 dark:bg-violet-900/30 dark:text-violet-100"
              >
                {previewNotice.verb}
              </button>
              {!dismissedNotices.some(d => d.key === previewNotice.key) && (
                <button
                  type="button"
                  disabled={noticeBusy}
                  onClick={() => answerNotice(previewNotice, 'dismiss')}
                  className="text-xs text-gray-500 hover:underline disabled:opacity-50 dark:text-gray-400"
                >
                  Not interesting
                </button>
              )}
            </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <NoticePreview
                boardroomId={id}
                notice={previewNotice}
                onOpen={openById}
                onEdit={editById}
                roomName={room.name}
              />
            </div>
          </div>
        </Drawer>
      )}
      {compareOpen && (
        <Drawer onClose={() => setCompare(null, true)} width="w-[72rem] max-w-full" eyebrow={room.name} title="Compare">
          <ComparePanel
            boardroomId={id}
            initial={parseCompare(compareParam)}
            onPicks={ids => setCompare(ids, true)}
            // THE LENS NARROWS "Add a company" to the companies inside it; the
            // picks keep their columns (`boardroom/compare.py`).
            load={ids =>
              api.getBoardroomReport<BoardroomCompare>(id, 'compare', {
                subject: ids.join(','),
                tags: view.tags,
                verified: view.verified,
                sourced: view.sourced,
                by: view.by,
              })
            }
            lensKey={JSON.stringify([view.tags, view.verified, view.sourced, view.by])}
            onOpenItem={itemId => {
              setCompare(null, true);
              openById(itemId);
            }}
          />
        </Drawer>
      )}
      {/* A PANEL SLID OUT over the page: Escape or × puts it away, Pin docks it. */}
      {panels.overlay && (
        <Drawer
          onClose={() => panels.close(panels.overlay as PanelKey)}
          width={GROUP_WIDTH[groupOf(panels.overlay)]}
          eyebrow={room.name}
          title={PANEL_TITLE(panels.overlay)}
          fill
          actions={wide ? <PinButton pinned={false} onClick={() => panels.pin(panels.overlay as PanelKey)} /> : undefined}
        >
          {/* A full-height column, so a panel's own scrolling regions have a
              height to scroll in. Without it the feed grew past the bottom of
              the slide-out and was clipped, and could not be scrolled. */}
          <div className="h-full min-h-0 flex flex-col">
            <PanelTabs current={panels.overlay} labels={panelLabels} onPick={panels.switchTo} />
            {panelBody(panels.overlay)}
          </div>
        </Drawer>
      )}
      {/* ONLY WHILE SOMETHING IS LINED UP. A report's Dig button stages here,
          and a dig about to spend is worth seeing without opening anything;
          typing one of your own is in the room brief. */}
      {digModal && (
        <DigModal
          title={digModal.title}
          dig={digModal.dig}
          aim={digModal.aim}
          scopes={digModal.scopes}
          onAim={reaim}
          recommended={room.next_dig ?? null}
          onResearch={
            digModal.aim && digModal.scopes
              ? () => runResearch(digModal.aim!, digModal.scopes!)
              : undefined
          }
          onChange={dig => setDigModal(m => (m ? { ...m, dig } : m))}
          onCancel={() => setDigModal(null)}
          onDig={dig => {
            setDigModal(null);
            return startDig(dig, clampBudget(budget, maxBudget));
          }}
          digging={room.run?.status === 'RUNNING'}
          budgetPicker={
            <BudgetPicker
              budget={budget}
              onBudget={setBudget}
              maxBudget={maxBudget}
              budgetCappedByCredits={budgetCappedByCredits}
              creditsLine={creditsLine(credits, priceBook)}
            />
          }
        />
      )}
      {stagedHasContent(staged) && (
        <div className="mb-2">
          <NextDig
            staged={staged}
            onChange={setStaged}
            onStart={directive => startStaged(budget, directive)}
            onClear={() => setStaged(null)}
            digging={room.run?.status === 'RUNNING'}
            budgetLabel={`${BUDGET_OPTIONS.find(b => b.tokens === budget)?.label ?? budget.toLocaleString()} tokens`}
          />
        </div>
      )}
      {error && (
        <div className="mb-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </div>
      )}
      {note && (
        <div className="mb-2 flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-300" role="status">
          <span className="flex-1">{note}</span>
          <button type="button" onClick={() => setNote(null)} className="shrink-0 hover:underline">
            dismiss
          </button>
        </div>
      )}

      <div
        className="grid min-h-0 min-w-0 shrink-0 gap-3"
        style={{
          // A FIXED HEIGHT, a little under the screen. The board measures this
          // to decide how many tables fit, so it cannot grow with its content.
          height: 'calc(100vh - 1rem)',
          // NO RIGHT RAIL, AND A CHAT COLUMN. The seats and the run control moved
          // into "In the room" (they are how the room works, not what it found),
          // and the conversation took the column instead: the board and the
          // talking about it are read together. Collapsed, the chat keeps a
          // 2rem edge tab, the way the feed's rail does on the left.
          gridTemplateColumns: `minmax(0, 1fr)${panels.docked ? ` 1px ${chat.width}px` : ''} 2rem`,
          // Animate a jump (a double-click reset), never a drag — a transition under
          // the pointer is the rubber-banding that makes a resize feel broken.
          transition:
            chat.dragging
              ? undefined
              : 'grid-template-columns 160ms ease',
        }}
      >
        <section className="min-h-0 flex flex-col gap-2">
          {/* ALWAYS SHOWN, so there is one place to look for where you are.
              Back goes to the previous step on OUR trail - by the browser's
              own Back when that is the same step (see `goBack`). */}
          <LocationBar
            roomId={id}
            roomName={room.name}
            path={locationPath}
            back={backLabel}
            onBack={goBack}
            visits={history}
            onPickVisit={v => goSaved(v.search)}
            onClearVisits={() => {
              setHistory([]);
              saveHistory(id, []);
            }}
            loadRooms={loadRooms}
            extra={
              <>
              {stage === 'profile' && profile?.item && (
                <KeepPage
                  card={board?.deliverables.find(
                    d => d.kind === 'profile' && d.subject === String(profile.item.id) && d.status !== 'dropped',
                  )}
                  onKeep={pinned => {
                    setError(null);
                    api
                      .addBoardroomDeliverable(id, { kind: 'profile', item_id: profile.item.id, pinned })
                      .then(fresh => {
                        setBoard(fresh);
                        setNote(
                          pinned
                            ? `${profile.item.title} is on the Dashboard.`
                            : `${profile.item.title} is kept with your views. Pin it from All views to see it on the Dashboard.`,
                        );
                      })
                      .catch(e => setError(e instanceof Error ? e.message : 'Could not keep this page'));
                  }}
                />
              )}
              {/* THE WAY HOME, as on an open view: a company's or a product's
                  page is somewhere you went from the Dashboard, and the crumb
                  above is easy to miss. */}
              {stage === 'profile' && (
                <button
                  type="button"
                  onClick={() => go({ ...place, stage: 'board', expanded: null })}
                  className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  data-testid="profile-to-dashboard"
                >
                  ‹ Dashboard
                </button>
              )}
              <TableFilterBox value={tableFilter} onChange={setTableFilter} />
              <FindBox
                targets={findTargets}
                searchItems={searchItems}
                onItem={row =>
                  (PROFILE_KINDS as readonly string[]).includes(row.kind) ? openProfile(row.id) : setOpen(row)
                }
              />
              </>
            }
          />
          {/* WHAT YOU ARE LOOKING THROUGH, in one box under where you are: the
              lead the room follows, and the lens - its held chips and any you
              added. The crumbs above say which page; this says through what. */}
          {room.lead ? (
            <div
              className="shrink-0 overflow-hidden rounded-lg border border-indigo-200 dark:border-indigo-800"
              data-testid="lead-and-lens"
            >
              <LeadBar
                key={room.lead.id}
                lead={room.lead}
                busy={leadBusy}
                onGo={leadId => void goLead(leadId)}
                onEdit={editLead}
                onDirection={() => void leadToDirection()}
                embedded
              />
              <LensBar
                view={view}
                onView={setView}
                personas={personas}
                lead={room.lead}
                onFollow={() => setFollowOpen(true)}
                embedded
                termFacets={facets?.term_facets ?? null}
                roomTagFacets={room.tag_facets}
                total={lensTotal}
              />
            </div>
          ) : (
            <LensBar
              view={view}
              onView={setView}
              personas={personas}
              lead={null}
              onFollow={() => setFollowOpen(true)}
              termFacets={facets?.term_facets ?? null}
              roomTagFacets={room.tag_facets}
              total={lensTotal}
            />
          )}
          {followOpen && (
            <FollowLead
              key={view.tags.join('|')}
              label={<LensPhrase tags={view.tags} verified={view.verified ?? null} by={view.by ?? null} />}
              inside={room.lead?.title ?? null}
              draft={() => api.draftBoardroomLead(id, lensOfView(view))}
              onFollow={followLens}
              onCancel={() => setFollowOpen(false)}
            />
          )}
          {stage === 'board' && (
            <DeliverableBoard
              roomKind={room.room_kind}
              // THE BRIEF, above the cards: the top lines of the tables, under
              // the same lens. Reloaded when a dig starts or ends and on the
              // room's slowed tick, not on every item that lands.
              top={
                <RoomBrief
                  roomKind={room.room_kind}
                  load={() =>
                    api.getBoardroomReport<BoardroomRoomBrief>(id, 'room_brief', {
                      tags: view.tags,
                      verified: view.verified,
                      sourced: view.sourced,
                      by: view.by,
                    })
                  }
                  refreshKey={`${id}:${room.run?.id ?? ''}:${room.run?.status ?? ''}:${facetsTick}:${JSON.stringify([view.tags, view.verified, view.sourced, view.by])}`}
                  onOpenTable={kind => openView(kind, '')}
                  onOpenItem={openById}
                  onFill={research}
                  // WHAT THE ROOM NOTICED, here rather than behind the Noticed
                  // button: the observer's findings and the sites that keep
                  // coming up. Complaints are the Brief's own section.
                  noticed={
                    <BriefNoticed
                      observations={observations}
                      notices={notices}
                      busy={observationBusy || noticeBusy}
                      onSeen={o => void markObservationSeen(o)}
                      onDismiss={n => void answerNotice(n, 'dismiss')}
                      onShowRanks={() => openView('who_ranks', '')}
                      onOpenItem={openById}
                      onMore={() => setBoardPanel('observed')}
                      // Brought back from the Noticed panel, which has no
                      // button of its own now.
                      setAside={dismissedNotices.length}
                      onSetAside={() => setBoardPanel('noticed')}
                    />
                  }
                />
              }
              deliverables={board?.deliverables ?? []}
              // A view's tab for a table the room has not kept opens it unsaved.
              onOpenKind={kind => openView(kind, '', true)}
              onCompare={isStoryRoom(room.room_kind) ? undefined : () => openCompare()}
              catalogue={board?.catalogue ?? []}
              badges={deliverableBadges}
              renderCard={renderDeliverableCard}
              matches={boardMatches}
              // OPENING A TABLE IS A STEP: Back, and the browser's Back, return
              // to where you opened it from. The board's own moves replace.
              renderFull={renderDeliverableFull}
              expanded={expandedKind}
              onExpand={(d, replace) => openOnBoard(d, Boolean(replace))}
              // ON THE LIST OR OFF IT. Off, it waits in Add.
              onDelete={d => void deleteCard(d)}
              temporary={temporaryView}
              onSave={d => void keepTemporary(d, false)}
              // SHARING ONE VIEW: the chat's own lists and reports. The room as a
              // whole is shared from the top bar.
              renderActions={d =>
                d.id > 0 && (d.kind === 'list' || d.kind === 'report') ? (
                  <ShareArtifactControl kind="BOARDROOM_CARD" boardroomDeliverableId={d.id} className="shrink-0" />
                ) : null
              }
              onPin={(d, pinned) => {
                if (d.id < 0) return void keepTemporary(d, pinned);
                void pinCard(d, pinned);
                if (!pinned) setNote(`${d.title} is unpinned from the Dashboard. It's in All views if you want it back.`);
              }}
              // Only what moved is written — a drag is one or two PATCHes, and
              // the board shows the new order before they land.
              onReorder={positions => {
                setBoard(b =>
                  b
                    ? {
                        ...b,
                        deliverables: b.deliverables.map(d =>
                          positions[d.id] === undefined
                            ? d
                            : { ...d, position: positions[d.id] },
                        ),
                      }
                    : b,
                );
                // Positions only, and the last reply wins — same ticket as a
                // pin, because a drag is several patches at once.
                const seq = ++patchSeq.current;
                Object.entries(positions).forEach(([deliverableId, position]) =>
                  api
                    .patchBoardroomDeliverable(id, Number(deliverableId), { position })
                    .then(fresh => {
                      if (seq === patchSeq.current) setBoard(fresh);
                    })
                    .catch(() => loadBoard()),
                );
              }}
              panel={boardPanel}
              onPanel={setBoardPanel}
              panels={{
                noticed: {
                  count: notices.length,
                  body:
                    notices.length || dismissedNotices.length || undoNotice ? (
                      <NoticeTray
                    notices={notices}
                    busy={noticeBusy}
                    onTake={n => answerNotice(n, 'take')}
                    onDismiss={n => answerNotice(n, 'dismiss')}
                    dismissed={dismissedNotices}
                    undo={undoNotice}
                    onPreview={setPreviewNotice}
                    onRestore={key => answerNotice({ key }, 'restore')}
                  />
                    ) : null,
                },
                observed: {
                  count: observations.length,
                  body: observations.length ? (
                    <ObservationTray
                    observations={observations}
                    busy={observationBusy}
                    onOpen={openById}
                    onOpenPage={openProfile}
                    onSeen={markObservationSeen}
                  />
                  ) : null,
                },
                add: (
                  <ViewPicker
                    roomKind={room.room_kind}
                    deliverables={board?.deliverables ?? []}
                    catalogue={board?.catalogue ?? []}
                    onAdd={kind => void addView(kind)}
                    onOpen={kind => {
                      setBoardPanel(null);
                      openView(kind, '');
                    }}
                    // A VIEW THAT IS NOT HERE is the chat's to build, with
                    // the ask already written so it can be qualified first.
                    onDescribe={text => {
                      setBoardPanel(null);
                      setChatOpen(true);
                      setChatDraft(`Build a view: ${text}`);
                    }}
                    everything={browserFor(() => setBoardPanel(null), true)}
                  />
                ),
              }}
            />
          )}
          {stage === 'profile' &&
            (profile ? (
              <ProfileView
                data={profile}
                onCompare={profile.item.kind === 'COMPANY' ? () => openCompare(profile.item.id) : undefined}
                lastDig={<LastDigSummary boardroomId={id} itemId={profile.item.id} refreshKey={profile} onShowRun={showRun} />}
                loadFilings={itemId => api.getBoardroomItemFilings(id, itemId)}
                // NOT A VIDEO OF THIS: ruled out, kept on the board with that
                // judgement, and gone from the page once it reloads.
                onRuleOutVideo={videoId => {
                  setError(null);
                  api
                    .updateBoardroomItem(id, videoId, { status: 'KILLED' })
                    .then(() => setProfile(p => (p && 'videos' in p ? { ...p, videos: (p.videos ?? []).filter(v => v.id !== videoId) } : p)))
                    .catch(e => setError(e instanceof Error ? e.message : 'Could not take that video off'));
                }}
                // Built here rather than inside ProfileView: that file is unit-tested and must not
                // import the router. Only a company or a product can be watched.
                watchControl={
                  // The watch reports once it exists; the button argues for one until then. The
                  // server sends whichever applies, so this mirrors that rather than deciding.
                  profile.watch_live ? (
                    <WatchPanel live={profile.watch_live} returnLabel={room.name} />
                  ) : profile.item.kind === 'COMPANY' || profile.item.kind === 'PRODUCT' ? (
                    <TrackThis
                      boardroomId={room.id}
                      itemId={profile.item.id}
                      watchSubjectId={profile.item.watch_subject_id}
                      hasUrl={Boolean(profile.item.url)}
                      preview={profile.watch_preview}
                      returnLabel={room.name}
                    />
                  ) : undefined
                }
                // WHO IS GOING AFTER ITS SEARCHES. Companies only: the
                // searches are built from a company's name and products.
                brandControl={
                  profile.item.kind === 'COMPANY' ? (
                    <BrandSection
                      boardroomId={room.id}
                      itemId={profile.item.id}
                      name={profile.item.title}
                      summary={'brand' in profile ? profile.brand : null}
                      onStarted={() => {
                        setFacetsTick(t => t + 1);
                        loadBoard();
                      }}
                      onOpenReport={() => openCard('brand', String(profile.item.id))}
                      onOpenItem={openById}
                    />
                  ) : undefined
                }
                onOpen={openById}
                // A FEATURE'S NEAR-COPIES: fold one into this feature, or say
                // it is a different thing. The page reloads either way.
                // ALL THE TICKED ONES, ONE RELOAD AT THE END. One at a time, so
                // two merges never race over the same links; a failure is
                // counted and the rest still go.
                onMerge={async otherIds => {
                  let failed = 0;
                  for (const otherId of otherIds) {
                    try {
                      await api.mergeBoardroomItem(id, otherId, profile.item.id);
                    } catch {
                      failed += 1;
                    }
                  }
                  if (failed) setError(`Could not merge ${failed} of ${otherIds.length}`);
                  setFacetsTick(t => t + 1);
                }}
                onKeepApart={async otherIds => {
                  let failed = 0;
                  for (const otherId of otherIds) {
                    try {
                      await api.keepBoardroomItemsApart(id, profile.item.id, otherId);
                    } catch {
                      failed += 1;
                    }
                  }
                  if (failed) setError(`Could not mark ${failed} of ${otherIds.length} as different`);
                  setFacetsTick(t => t + 1);
                }}
                onPickTag={toggleLensTag}
                lensTags={view.tags}
                onEdit={editById}
                // THE PAGE'S OWN DIG — the one at the top, about this item —
                // is written as a brief. A dig into one missing row below stays
                // the plain line it was.
                onDig={(directive, intent, focus) =>
                  focus?.scope === 'table'
                    ? draftDig({ kind: 'item', itemId: profile.item.id }, profile.item.title, () => templateDig(directive, intent, focus, 'a Dig button'))
                    : stageDig(directive, intent, focus, 'a Dig button')
                }
                unseen={buckets.competitors}
                onSeen={ids => setBuckets(b => markSeen(b, 'competitors', ids))}
                digging={room.run?.status === 'RUNNING'}
                personas={personas}
              />
            ) : (
              <div className="text-xs text-gray-400 py-6">Reading the board…</div>
            ))}
        </section>

        {panels.docked && (
          <ResizeHandle
            grabber
            onPointerDown={chat.onPointerDown}
            dragging={chat.dragging}
            className="h-full"
            title="Drag to size the panel"
          />
        )}
        {panels.docked && (
          <DockedPanel
            title={PANEL_TITLE(panels.docked)}
            onUnpin={panels.unpin}
            onClose={() => panels.close(panels.docked as PanelKey)}
            // The chat draws its own header; Unpin and × go into it.
            bare={panels.docked === 'chat'}
          >
            <PanelTabs current={panels.docked} labels={panelLabels} onPick={panels.switchTo} />
            {panelBody(panels.docked, true)}
          </DockedPanel>
        )}
        <RightRail tabs={railTabs} docked={panels.docked} overlay={panels.overlay} onPick={panels.toggleGroup} />

      </div>

      {open && (
        <ItemDrawer
          boardroomId={id}
          item={open}
          personas={personas}
          canPublish={has('PUBLISH')}
          onClose={() => setOpen(null)}
          onChanged={next => {
            upsert(next);
            replaceOpen(next);
          }}
          onOpen={pushOpen}
          trail={drawerTrail}
          onBack={() => setDrawerTrail(t => t.slice(0, -1))}
          onProfile={itemId => {
            setOpen(null);
            openProfile(itemId);
          }}
        />
      )}
    </div>
    </LensTagsContext.Provider>
    </TableFilterContext.Provider>
    </FreshProvider>
  );
}
